"use client";
import { useState, useEffect, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { generateJWTToken } from "@/utils/helpers/helperFunctions";
import { useAuth } from "@/contexts/authContext";
import { ethers } from "ethers";
import { supabase } from "@/supabase/client";
import { toast } from "react-toastify";
import Loading from "@/app/loading";
import erc20Abi from "@/components/abi/erc20ABI.json";
import Icon from "@/components/ui/Icon";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { fetchNodeHostingData, fetchWalletReports } from "@/utils/firebasegeneral";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";


// FAQ data
const faqData = [
  {
    question: "What are credits?",
    answer:
      "Credits power our cloud infrastructure, which runs all our games and ecosystem services. Unlike typical Web3 projects that rely on blockchain nodes—often requiring costly hardware and high energy use—our system uses lightweight cloud-based servers, databases, and compute resources. This gives you a more efficient experience: simply keep your credits topped up (at a low cost), and you'll continue earning daily XSEED rewards without hassle.",
  },
  {
    question: "How many credits are deducted per day?",
    answer:
      "The daily credit deduction may evolve over time. Currently, 10 credits are deducted each night for every full node licence you hold. For example, if you own 3 full node licences, 30 credits will be deducted nightly. Fractional node licences come with prepaid credits, so no deductions apply to them.",
  },
  {
    question: "When are credits deducted?",
    answer: "Credits are automatically deducted at midnight UTC every night.",
  },
  {
    question: "What happens if I run out of credits?",
    answer:
      "If your credit balance reaches zero, you won't be eligible to claim XSEED rewards for that day. Once you top up your credits, XSEED generation will automatically resume. You're always in control.",
  },
  {
    question: "Can I recover missed XSEED rewards if I had no credits?",
    answer:
      "No. XSEED rewards missed due to insufficient credits cannot be recovered.",
  },
  {
    question: "What happens to XSEED tokens that weren't distributed?",
    answer:
      "Unclaimed XSEED tokens from missed days will either be permanently removed from circulation (burned) or locked for long-term holding. This helps protect the token's value.",
  },
  {
    question: "Where can I get help with purchasing credits?",
    answer:
      "If you experience any issues when buying credits, please submit a support request on our website, select 'Node Hosting' in Issue Area.",
  },
  {
    question: "Are credits refundable?",
    answer:
      "No, credits are non-refundable. Please keep this in mind before making a purchase.",
  },
  {
    question: "What credit packages are available and how much do they cost?",
    answer:
      "We offer four credit packages: Spark, Blaze, Boost, and Inferno. The larger the package, the greater the discount—Inferno offers up to 50% off. This allows you to get more value the more you buy.",
  },
];

// Constants
const CACHE_EXPIRY = 300000; // 5 minutes in ms
const API_ENDPOINTS = {
  products:
    "https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/get-products",
  paymentIntent:
    "https://us-central1-prodmxs.cloudfunctions.net/createPaymentIntent",
  conversionRate:
    "https://us-central1-prodmxs.cloudfunctions.net/get_active_conversion_rate",
  adminWallet: "https://us-central1-prodmxs.cloudfunctions.net/get_wallet",
};
const STRIPE_THEME = {
  theme: "night",
  variables: {
    colorPrimary: "#6366f1",
    colorBackground: "#1f2937",
    colorText: "#f3f4f6",
    borderRadius: "8px",
    spacingUnit: "8px",
  },
  rules: {
    ".Input": { border: "1px solid #374151", padding: "12px" },
  },
};
const PAYMENT_METHODS = {
  crypto: {
    label: "Crypto",
    icon: "https://res.cloudinary.com/dynb4avhf/image/upload/v1742187688/generic-cryptocurrency.512x508_unjfpj.png",
  },
  stripe: {
    label: "Credit Card",
    icon: "https://res.cloudinary.com/dynb4avhf/image/upload/v1741268466/card_pt5r8e.png",
  },
};
// Removed hardcoded ADMIN_WALLET. Will fetch dynamically.
// const ADMIN_WALLET = "0xA0329AD5425EfE649c4E8dfb662c4Af613DdF3F0";

const SkeletonHostingData = () => (
  <div className="space-y-6">
    <div className="bg-[#252525] rounded-lg p-6 border border-[#3a3a3c]">
      <div className="flex items-center mb-4">
        <Skeleton
          width={120}
          height={14}
          baseColor="#374151"
          highlightColor="#4B5563"
        />
      </div>
      <div className="grid grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i}>
            <Skeleton
              width={80}
              height={14}
              className="mb-2"
              baseColor="#374151"
              highlightColor="#4B5563"
            />
            <Skeleton
              width={60}
              height={14}
              baseColor="#374151"
              highlightColor="#4B5563"
            />
          </div>
        ))}
      </div>
    </div>

    <div className="bg-[#252525] rounded-lg p-6 border border-[#3a3a3c]">
      <div className="flex items-center mb-4">
        <Skeleton
          width={120}
          height={14}
          baseColor="#374151"
          highlightColor="#4B5563"
        />
      </div>
      <div className="grid grid-cols-2 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i}>
            <Skeleton
              width={100}
              height={14}
              className="mb-2"
              baseColor="#374151"
              highlightColor="#4B5563"
            />
            <Skeleton
              width={80}
              height={14}
              baseColor="#374151"
              highlightColor="#4B5563"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Update the PaymentForm component
const PaymentForm = ({
  onSuccess,
  onError,
  setShowPaymentModal,
  setPaymentModalContent,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    // Show processing modal
    setShowPaymentModal(true);
    setPaymentModalContent({
      title: "Processing Card Payment",
      message: "Please wait while we process your payment...",
      status: "processing",
      details: null,
    });

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/complete`,
        },
        redirect: "if_required",
      });

      if (error) {
        console.error("Payment error:", error);
        onError(error.message || "Payment failed. Please try again.");
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error("Payment processing error:", err);
      onError("An unexpected error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        onChange={(event) => {
          setIsCardComplete(event.complete);
        }}
        options={{
          layout: "tabs",
        }}
      />
      <button
        type="submit"
        disabled={!stripe || isProcessing || !isCardComplete}
        className={`w-full font-semibold py-3 px-4 rounded-lg transition duration-200 ease-in-out flex items-center justify-center gap-2 mt-4 ${
          !stripe || isProcessing || !isCardComplete
            ? "bg-gray-600 text-gray-300 cursor-not-allowed"
            : "bg-indigo-600 hover:bg-indigo-700 text-white"
        }`}
      >
        {isProcessing ? (
          <>
            <Icon icon="eos-icons:loading" className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : !isCardComplete ? (
          <>
            <Icon
              icon="material-symbols:credit-card-outline"
              className="w-5 h-5"
            />
            Complete Card Details
          </>
        ) : (
          <>
            <Icon
              icon="material-symbols:payments-outline"
              className="w-5 h-5"
            />
            Pay Now
          </>
        )}
      </button>
    </form>
  );
};

// Update the renderFAQContent function
const renderFAQContent = (faqData, activeFaqIndex, setActiveFaqIndex) => (
  <div className="bg-metaxseed-dark-gray">
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {faqData.map((faq, index) => (
        <div
          key={index}
          className="border border-[#3a3a3c] rounded-lg overflow-hidden"
        >
          <div
            className={`px-4 py-3 flex justify-between items-center cursor-pointer ${
              activeFaqIndex === index
                ? "bg-[#1a2d41] border-b border-[#3a3a3c]"
                : "bg-[#1a1a1a]"
            }`}
            onClick={() =>
              setActiveFaqIndex(activeFaqIndex === index ? null : index)
            }
          >
            <div className="flex items-center gap-2">
              <Icon
                icon="material-symbols:help-outline-rounded"
                className="w-5 h-5 text-metaxseed-open-blue flex-shrink-0"
              />
              <h3 className="text-sm font-medium text-gray-300 gothamMedium">
                {faq.question}
              </h3>
            </div>
            <Icon
              icon="material-symbols:keyboard-arrow-down-rounded"
              className={`w-5 h-5 transition-transform ${
                activeFaqIndex === index ? "rotate-180" : ""
              }`}
            />
          </div>
          {activeFaqIndex === index && (
            <div className="px-4 py-3 bg-[#111] text-gray-300 text-sm leading-relaxed">
              <p>{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// Add this skeleton card component for package loading
const SkeletonPackageCard = () => (
  <div className="rounded-lg overflow-hidden border border-[#3a3a3c] animate-pulse">
    <div className="py-2 bg-[#4B5563] text-center">
      <div className="h-4 w-20 mx-auto bg-gray-700 rounded" />
    </div>
    <div className="p-4 text-center bg-[#1a1a1a]">
      <div className="h-5 w-16 mx-auto bg-gray-700 rounded mb-2" />
      <div className="h-8 w-20 mx-auto bg-gray-700 rounded mb-2" />
      <div className="h-3 w-16 mx-auto bg-gray-700 rounded mb-4" />
      <div className="h-5 w-24 mx-auto bg-gray-700 rounded mb-4" />
      <div className="h-8 w-full bg-gray-700 rounded" />
    </div>
  </div>
);

function ClaimFreeCreditCard({ walletAddress, ipAddress }) {
  const [claimData, setClaimData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    message: "",
    success: false,
  });
  const [todoStatus, setTodoStatus] = useState(null);
  const [todoLoading, setTodoLoading] = useState(false);
  const [todoError, setTodoError] = useState(null);
  // Simple report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const limit = 10;

  const API_KEY = "93b0b239-1603-4781-91cf-e5d43e6dfe4e";

  // Fetch claim data
  useEffect(() => {
    if (!walletAddress) return;
    setLoading(true);
    fetch(
      `https://us-central1-prodmxs.cloudfunctions.net/get_latest_claim?wallet=${walletAddress}`,
      {
        headers: { Authorization: API_KEY },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setClaimData(data);
        setLoading(false);

        // Calculate time until next midnight UTC (always)
        const now = new Date();
        const nextMidnightUTC = new Date(now);
        nextMidnightUTC.setUTCDate(nextMidnightUTC.getUTCDate() + 1);
        nextMidnightUTC.setUTCHours(0, 0, 0, 0);

        const secondsUntilMidnight = Math.max(
          0,
          Math.floor((nextMidnightUTC.getTime() - now.getTime()) / 1000)
        );

        console.log("Countdown calculation:", {
          currentUTC: now.toISOString(),
          currentUTCTime: `${now.getUTCHours()}:${now
            .getUTCMinutes()
            .toString()
            .padStart(2, "0")} UTC`,
          nextMidnightUTC: nextMidnightUTC.toISOString(),
          secondsUntilMidnight: secondsUntilMidnight,
          hoursUntilMidnight: Math.floor(secondsUntilMidnight / 3600),
          minutesUntilMidnight: Math.floor((secondsUntilMidnight % 3600) / 60),
          backend_next_claim: data.next_claim_available_at,
        });

        setCountdown(secondsUntilMidnight);
      })
      .catch((err) => {
        setError("Failed to fetch claim info");
        setLoading(false);
      });
  }, [walletAddress]);

  // Fetch user task status
  useEffect(() => {
    if (!walletAddress) return;
    setTodoLoading(true);
    setTodoError(null);
    fetch(
      `https://us-central1-prodmxs.cloudfunctions.net/get_user_task_status?wallet=${walletAddress}`,
      {
        headers: { Authorization: API_KEY },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setTodoStatus(data);
        setTodoLoading(false);
      })
      .catch((err) => {
        setTodoError("Failed to fetch task status");
        setTodoLoading(false);
      });
  }, [walletAddress]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const interval = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [countdown]);

  // Format seconds to time object
  const formatTime = (secs) => {
    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;
    return { hours, minutes, seconds };
  };

  // Claim handler
  async function handleClaim() {
    if (!claimData || !walletAddress || !ipAddress) {
      console.log("Missing required data:", {
        claimData,
        walletAddress,
        ipAddress,
      });
      return;
    }

    console.log("Claim data:", claimData);
    console.log("Sending claim request with:", {
      id: claimData.id,
      ip_address: ipAddress,
      amount: claimData.claimable_amount,
      wallet: walletAddress,
    });

    setClaiming(true);
    setError(null);

    // Show processing modal immediately
    setModal({
      open: true,
      message: "Processing your claim request...",
      success: null, // null means processing
      processing: true,
    });

    try {
      const requestBody = {
        id: claimData.id,
        ip_address: ipAddress,
        amount: claimData.claimable_amount,
        wallet: walletAddress,
      };

      const res = await fetch(
        "https://us-central1-prodmxs.cloudfunctions.net/claim_credit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: API_KEY,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await res.json();
      console.log("API response:", data);

      if (res.ok) {
        setModal({
          open: true,
          message: data.message || "Credits claimed successfully!",
          success: true,
          processing: false,
          serverData: data
        });

        setTimeout(() => {
          if (walletAddress) {
            fetch(
              `https://us-central1-prodmxs.cloudfunctions.net/get_latest_claim?wallet=${walletAddress}`,
              {
                headers: { Authorization: API_KEY },
              }
            )
              .then((res) => res.json())
              .then((refreshedData) => {
                setClaimData(refreshedData);
                
                // After claim data is refreshed, fetch Node Hosting Data
                return fetchNodeHostingData(walletAddress);
              })
              .then((hostingFeesData) => {
                if (hostingFeesData) {
                  setHostingData(hostingFeesData.data || null);
                }
                
                // Calculate time until next midnight UTC
                const now = new Date();
                const nextMidnightUTC = new Date(now);
                nextMidnightUTC.setUTCDate(nextMidnightUTC.getUTCDate() + 1);
                nextMidnightUTC.setUTCHours(0, 0, 0, 0);
                
                const secondsUntilMidnight = Math.max(
                  0,
                  Math.floor((nextMidnightUTC.getTime() - now.getTime()) / 1000)
                );
                
                setCountdown(secondsUntilMidnight);
              })
              .catch((err) => console.error("Error refreshing data:", err));
          }
        }, 500);
      } else {
        console.error("API error:", data);
        // Show error modal with server error
        setModal({
          open: true,
          message: data.error || "Failed to claim credits. Please try again.",
          success: false,
          processing: false,
          serverData: data,
        });
      }
    } catch (e) {
      console.error("Claim error:", e);
      // Show network error modal
      setModal({
        open: true,
        message: "Network error. Please check your connection and try again.",
        success: false,
        processing: false,
        serverData: null,
      });
    }
    setClaiming(false);
  }

  const timeLeft = formatTime(countdown);
  const isClaimable = countdown === 0 && claimData?.claimable_amount > 0;

  // Calculate progress percentage more accurately
  const calculateProgressPercentage = () => {
    if (countdown <= 0) return 100;

    // If we have next_claim_available_at, calculate based on actual time period
    if (claimData?.next_claim_available_at) {
      const nextClaimTime = new Date(
        claimData.next_claim_available_at
      ).getTime();
      const now = Date.now();

      // Assume 24 hour cycle (86400 seconds)
      const totalCycleDuration = 24 * 60 * 60; // 24 hours in seconds
      const timeElapsed = totalCycleDuration - countdown;
      return Math.min(
        100,
        Math.max(0, (timeElapsed / totalCycleDuration) * 100)
      );
    }

    // Fallback to original calculation
    return countdown > 0 ? ((86400 - countdown) / 86400) * 100 : 100;
  };

  const progressPercentage = calculateProgressPercentage();

  // Fetch last 5 reports when modal opens
  useEffect(() => {
    if (reportModalOpen && walletAddress) {
      fetchReports();
    }
  }, [reportModalOpen, walletAddress, currentPage]);

  const fetchReports = async () => {
    if (!walletAddress) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const response = await fetchWalletReports(walletAddress, currentPage, limit);
      if (response && response.success) {
        setReportRows(response.data || []);
        setPagination(response.pagination || null);
      } else {
        setReportError("Failed to load reports");
      }
    } catch (err) {
      console.error("Error fetching reports:", err);
      setReportError("An error occurred while fetching reports");
    } finally {
      setReportLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && (!pagination || page <= pagination.total_pages)) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Main Card */}
      <div className="bg-[#1a1a1a]/80 backdrop-blur-lg border border-[#3a3a3c] rounded-xl shadow-2xl">
        <div className="p-6 space-y-6">
          {/* Header inside card */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center">
              <h2 className="text-xl font-bold text-white gothamMedium">FREE DAILY CREDITS</h2>
            </div>
            <p className="text-gray-400">
              Claim your free credits Timelimit / Daily refresh every midnight UTC
            </p>
          </div>

          {loading ? (
            <div className="text-center space-y-4">
              <Icon
                icon="eos-icons:loading"
                className="w-8 h-8 animate-spin mx-auto text-metaxseed-open-blue"
              />
              <p className="text-gray-400">Loading claim data...</p>
            </div>
          ) : error ? (
            <div className="text-center space-y-4">
              <Icon
                icon="material-symbols:error-outline"
                className="w-8 h-8 mx-auto text-red-400"
              />
              <p className="text-red-400">{error}</p>
            </div>
          ) : (
            <>
              {/* Current Credits */}
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 text-yellow-500">
                  <Icon
                    icon="material-symbols:monetization-on-outline"
                    className="w-6 h-6"
                  />
                  <span className="text-sm font-medium">Available Credits</span>
                </div>
                <div className="text-6xl font-bold text-white">
                  {claimData?.claimable_amount || 0}
                </div>
              </div>

              {/* Timer Section - always show countdown to 12 AM UTC */}
              {countdown > 0 && (
                <div className="space-y-4">
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center gap-2 text-metaxseed-open-blue">
                      <Icon
                        icon="material-symbols:schedule"
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Next claim in</span>
                    </div>
                    <div className="flex justify-center gap-2">
                      <div className="bg-metaxseed-blue/20 border border-metaxseed-blue/30 rounded-lg p-3 min-w-[60px]">
                        <div className="text-2xl font-bold text-white">
                          {timeLeft.hours.toString().padStart(2, "0")}
                        </div>
                        <div className="text-xs text-gray-400">Hours</div>
                      </div>
                      <div className="bg-metaxseed-blue/20 border border-metaxseed-blue/30 rounded-lg p-3 min-w-[60px]">
                        <div className="text-2xl font-bold text-white">
                          {timeLeft.minutes.toString().padStart(2, "0")}
                        </div>
                        <div className="text-xs text-gray-400">Minutes</div>
                      </div>
                      <div className="bg-metaxseed-blue/20 border border-metaxseed-blue/30 rounded-lg p-3 min-w-[60px]">
                        <div className="text-2xl font-bold text-white">
                          {timeLeft.seconds.toString().padStart(2, "0")}
                        </div>
                        <div className="text-xs text-gray-400">Seconds</div>
                      </div>
                    </div>
                  </div>
                  {/* Required Tasks for Claiming - inside card, below countdown */}
                  {todoStatus?.is_todo_required && (
                    <div className="bg-[#181f2a] rounded-xl border-2 border-metaxseed-blue/40 shadow-lg mt-4">
                      <div className="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue px-4 py-2 rounded-t-xl flex items-center gap-2">
                        <Icon icon="material-symbols:checklist" className="w-5 h-5 text-white" />
                        <span className="text-white font-bold text-base gothamMedium">Required Tasks for Claiming</span>
                      </div>
                      <div className="p-4">
                        {todoLoading ? (
                          <div className="text-gray-400 flex items-center gap-2"><Icon icon="eos-icons:loading" className="w-5 h-5 animate-spin" />Loading tasks...</div>
                        ) : todoError ? (
                          <div className="text-red-400">{todoError}</div>
                        ) : todoStatus.tasks && todoStatus.tasks.length > 0 ? (
                          <div className="space-y-3">
                            {todoStatus.tasks.map((task, idx) => (
                              <div
                                key={task.id || idx}
                                className="bg-[#232b3a] rounded-lg p-3 border border-metaxseed-blue/20 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                              >
                                <div>
                                  <div className="text-metaxseed-open-blue font-bold text-base mb-1">{task.title}</div>
                                  <div className="text-gray-300 text-sm">{task.description}</div>
                                </div>
                                <span
                                  className={`text-xs font-bold rounded px-3 py-1 ml-2 mt-2 md:mt-0 md:ml-4 ${
                                    task.completed
                                      ? "bg-green-900 text-green-400"
                                      : "bg-yellow-900 text-yellow-400"
                                  }`}
                                >
                                  {task.completed ? "Completed" : "Not completed"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-gray-400">No active tasks found.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Success message when credits available */}
              {claimData?.claimable_amount > 0 && (
                <div className="text-center space-y-2">
                  <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full font-bold text-sm">
                    <Icon
                      icon="material-symbols:check-circle"
                      className="w-4 h-4"
                    />
                    Credits Ready to Claim!
                  </div>
                </div>
              )}

              {/* Claim Button - always show */}
              <button
                onClick={handleClaim}
                disabled={
                  claiming ||
                  (claimData?.claimable_amount <= 0 && claimData?.is_claimed !== false) ||
                  (todoStatus?.is_todo_required &&
                    todoStatus.tasks &&
                    todoStatus.tasks.some((task) => !task.completed))
                }
                className={`w-full py-4 text-lg shadow-lg transform transition-all duration-200 border-0 rounded-lg font-bold ${
                  claimData?.claimable_amount > 0 &&
                  !claiming &&
                  (!todoStatus?.is_todo_required ||
                    (todoStatus.tasks &&
                      todoStatus.tasks.every((task) => task.completed)))
                    ? "bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-orange-600 hover:to-yellow-500 text-white hover:scale-105"
                    : "bg-gray-600 text-gray-300 cursor-not-allowed"
                }`}
              >
                {claiming ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon
                      icon="eos-icons:loading"
                      className="w-5 h-5 animate-spin"
                    />
                    Claiming...
                  </div>
                ) : todoStatus?.is_todo_required &&
                  todoStatus.tasks &&
                  todoStatus.tasks.some((task) => !task.completed) ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon
                      icon="material-symbols:assignment"
                      className="w-5 h-5"
                    />
                    Complete your task
                  </div>
                ) : claimData?.claimable_amount > 0 ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon icon="material-symbols:redeem" className="w-5 h-5" />
                    Claim {claimData?.claimable_amount} Credits
                  </div>
                ) : claimData?.claimable_amount === 0 && claimData?.is_claimed === true ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon
                      icon="material-symbols:check-circle"
                      className="w-5 h-5"
                    />
                    Already Claimed
                  </div>
                ) : claimData?.claimable_amount === 0 && claimData?.is_claimed === false ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon icon="material-symbols:redeem" className="w-5 h-5" />
                    Claim 0 Credits
                  </div>
                ) : countdown > 0 ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon
                      icon="material-symbols:schedule"
                      className="w-5 h-5"
                    />
                    Next Claim in {Math.floor(countdown / 3600)}h{" "}
                    {Math.floor((countdown % 3600) / 60)}m
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Icon icon="material-symbols:block" className="w-5 h-5" />
                    No Credits Available
                  </div>
                )}
              </button>

              {/* Last Claimed */}
              <div className="text-center space-y-1 pt-2 border-t border-[#3a3a3c]">
                <div className="flex items-center justify-center gap-2 text-metaxseed-open-blue">
                  <Icon icon="material-symbols:event" className="w-4 h-4" />
                  <span className="text-sm">Last claimed</span>
                </div>
                <div className="text-sm text-gray-400">
                  {claimData?.last_claimed_at
                    ? new Date(claimData.last_claimed_at).toLocaleString()
                    : "Never claimed"}
                </div>
                {/* Simple Report Toggle Button */}
                {walletAddress && (
                  <button
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded bg-metaxseed-blue/20 hover:bg-metaxseed-blue/40 text-metaxseed-open-blue text-xs font-semibold border border-metaxseed-blue transition"
                    onClick={() => setReportModalOpen(true)}
                    type="button"
                  >
                    <Icon icon="mdi:clipboard-list-outline" className="w-4 h-4" />
                    View Recent Credit Activity
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Simple Report Modal */}
      <Modal
        activeModal={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        title="Recent Credit Activity"
        bodyClass="!py-4 !px-0"
        themeClass="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue !px-6 !py-4"
        className="w-full max-w-2xl bg-metaxseed-dark-gray rounded-lg shadow-lg"
      >
        {reportLoading ? (
          <div className="flex items-center justify-center py-10">
            <Icon icon="eos-icons:loading" className="w-8 h-8 text-metaxseed-open-blue animate-spin" />
            <span className="ml-3 text-gray-300">Loading...</span>
          </div>
        ) : reportError ? (
          <div className="bg-red-900/20 text-red-300 p-4 m-4 rounded-lg text-center">
            <p>{reportError}</p>
          </div>
        ) : (
          <div className="overflow-x-auto px-4 pb-4">
            <table className="min-w-full text-xs text-white">
              <thead>
                <tr className="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue/80">
                  <th className="py-2 px-2 text-left">Date</th>
                  <th className="py-2 px-2 text-left">Credit Claimed</th>
                  <th className="py-2 px-2 text-left">XSEED Claimed</th>
                </tr>
              </thead>
              <tbody>
                {reportRows.length > 0 ? (
                  reportRows.map((r) => {
                    return (
                      <tr key={r.id} className="border-t border-[#3a3a3c] hover:bg-[#232b3a] transition">
                        <td className="py-2 px-2 text-gray-300 text-xs">{r.date ? new Date(r.date).toLocaleDateString() : "-"}</td>
                        <td className="py-2 px-2 text-gray-300 text-xs">
                          {r.credit_added > 0 ? <span className="text-green-500 mr-1">✓</span> : <span className="text-red-500 mr-1">✗</span>}
                          {r.credit_added || 0}
                        </td>
                        <td className="py-2 px-2 text-gray-300 text-xs">
                          {r.claimable_amount > 0 ? <span className="text-green-500 mr-1">✓</span> : <span className="text-red-500 mr-1">✗</span>}
                          {Math.round(r.claimable_amount || 0)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="py-6 px-4 text-center text-gray-400">No recent credit activity</td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {pagination && pagination.total_pages > 0 && (
              <div className="px-4 py-3 border-t border-[#3a3a3c] flex flex-wrap items-center justify-between">
                <div className="text-xs text-gray-400 mb-2 md:mb-0">
                  {pagination.total_items > 0 ? (
                    <>
                      Showing items {((pagination.page - 1) * pagination.limit) + 1}-
                      {Math.min(pagination.page * pagination.limit, pagination.total_items)} of {pagination.total_items}
                    </>
                  ) : (
                    "No items to display"
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <div className="px-2 py-1 bg-[#1a1a1a] rounded border border-[#3a3a3c] flex items-center text-xs text-gray-400">
                    Page {pagination.page} of {pagination.total_pages}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className={`px-2 py-1 rounded text-xs flex items-center ${
                      currentPage <= 1
                        ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                        : "bg-metaxseed-blue text-white hover:bg-metaxseed-open-blue"
                    }`}
                  >
                    <Icon icon="bi:arrow-left" className="mr-1" width="10"/>
                    Prev
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= pagination.total_pages}
                    className={`px-2 py-1 rounded text-xs flex items-center ${
                      currentPage >= pagination.total_pages
                        ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                        : "bg-metaxseed-blue text-white hover:bg-metaxseed-open-blue"
                    }`}
                  >
                    Next
                    <Icon icon="bi:arrow-right" className="ml-1" width="10"/>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      {/* Confirmation Modal */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[99999] backdrop-blur-sm">
          <div className="bg-[#1a1a1a] rounded-xl border border-[#3a3a3c] shadow-2xl max-w-md w-full text-center mx-4 overflow-hidden">
            {/* Header with gradient */}
            <div
              className={`py-4 px-6 relative ${
                modal.processing
                  ? "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue"
                  : modal.success
                  ? "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue"
                  : "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue"
              }`}
            >
              <h3 className="text-lg font-bold text-white gothamMedium">
                {modal.processing
                  ? "Processing Claim"
                  : modal.success
                  ? "Claim Successful!"
                  : "Claim Failed"}
              </h3>
            </div>

            <div className="p-6">
              {/* Icon/Spinner */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center">
                {modal.processing ? (
                  <div className="relative">
                    <div className="w-20 h-20 border-4 border-metaxseed-blue/30 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-20 h-20 border-4 border-metaxseed-blue border-t-transparent rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon
                        icon="material-symbols:redeem"
                        className="w-8 h-8 text-metaxseed-blue"
                      />
                    </div>
                  </div>
                ) : modal.success ? (
                  <div className="bg-metaxseed-blue/20 w-20 h-20 rounded-full flex items-center justify-center">
                    <Icon
                      icon="material-symbols:check-circle"
                      className="w-12 h-12 text-metaxseed-open-blue"
                    />
                  </div>
                ) : (
                  <div className="bg-metaxseed-blue/20 w-20 h-20 rounded-full flex items-center justify-center">
                    <Icon
                      icon="material-symbols:error"
                      className="w-12 h-12 text-metaxseed-open-blue"
                    />
                  </div>
                )}
              </div>

              {/* Message */}
              <p className="text-gray-300 text-center mb-6 text-sm leading-relaxed">
                {modal.message}
              </p>

              {/* Success Data Display */}
              {modal.success && modal.serverData && (
                <div className="bg-[#0F1215] rounded-lg border border-metaxseed-blue/30 mb-6 overflow-hidden mt-6 text-left">
                  <div className="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue px-4 py-2">
                    <h4 className="text-white font-medium text-sm gothamMedium">Claim Details</h4>
                  </div>
                  <div className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Credits Claimed:</span>
                      <span className="text-white font-bold">{modal.serverData.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Previous Balance:</span>
                      <span className="text-white">{modal.serverData.balance?.previous ?? "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">New Balance:</span>
                      <span className="text-white">{modal.serverData.balance?.new ?? "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Balance:</span>
                      <span className="text-white">{modal.serverData.balance?.total ?? "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Current Hosting Credit:</span>
                      <span className="text-white">{modal.serverData.hosting?.current_credit ?? "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Hosting Credit:</span>
                      <span className="text-white">{modal.serverData.hosting?.total_credit ?? "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Hosting Days Left:</span>
                      <span className="text-white">{modal.serverData.hosting?.hosting_days_left !== undefined ? Math.round(modal.serverData.hosting.hosting_days_left) : "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Hosting Expiry:</span>
                      <span className="text-white">{modal.serverData.hosting?.hosting_expiry ? new Date(modal.serverData.hosting.hosting_expiry).toLocaleString() : "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Claimed At:</span>
                      <span className="text-white">{modal.serverData.claimed_at ? new Date(modal.serverData.claimed_at).toLocaleString() : "—"}</span>
                    </div>
                    {/* Optionally show wallet */}
                    {/* <div className="flex justify-between">
                      <span className="text-gray-400">Wallet:</span>
                      <span className="text-white">{modal.serverData.wallet}</span>
                    </div> */}
                  </div>
                </div>
              )}

              {/* Error Data Display */}
              {!modal.success &&
                !modal.processing &&
                modal.serverData &&
                (modal.serverData.error_code ||
                  modal.serverData.details ||
                  modal.message === "Task requirements not met") && (
                  <div className="bg-[#0F1215] rounded-lg border border-metaxseed-blue/30 mb-6 overflow-hidden">
                    <div className="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue px-4 py-2">
                      <h4 className="text-white font-medium text-sm gothamMedium">
                        Error Details
                      </h4>
                    </div>
                    <div className="p-4 space-y-3">
                      {modal.serverData.error_code && (
                        <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c] mb-2">
                          <span className="text-gray-400 text-sm">
                            Error Code:
                          </span>
                          <span className="text-metaxseed-open-blue font-bold text-sm">
                            {modal.serverData.error_code}
                          </span>
                        </div>
                      )}
                      {Array.isArray(modal.serverData.required_tasks) &&
                      modal.message === "Task requirements not met" ? (
                        <>
                          <p className="text-yellow-400 text-sm mb-2">
                            You must complete all required tasks before
                            claiming. Please complete your tasks and try again
                            later.
                          </p>
                          <div className="space-y-3">
                            {modal.serverData.required_tasks.map(
                              (task, idx) => (
                                <div
                                  key={task.id || idx}
                                  className="bg-[#181f2a] rounded-lg p-3 border border-metaxseed-blue/40"
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="text-metaxseed-open-blue font-bold text-base">
                                      {task.title}
                                    </div>
                                    <span
                                      className={`text-xs font-bold rounded px-2 py-1 ml-2 ${
                                        task.completed_today
                                          ? "bg-green-900 text-green-400"
                                          : "bg-yellow-900 text-yellow-400"
                                      }`}
                                    >
                                      {task.completed_today
                                        ? "Completed"
                                        : "Not completed"}
                                    </span>
                                  </div>
                                  <div className="text-gray-300 text-sm">
                                    {task.description}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </>
                      ) : modal.serverData.required_task &&
                        modal.message === "Task requirements not met" ? (
                        <>
                          <p className="text-yellow-400 text-sm mb-2">
                            You must complete your required task before
                            claiming. Please complete your task and try again
                            later.
                          </p>
                          <div className="bg-[#181f2a] rounded-lg p-3 border border-metaxseed-blue/40">
                            <div className="text-metaxseed-open-blue font-bold text-base mb-1">
                              {modal.serverData.required_task.title}
                            </div>
                            <div className="text-gray-300 text-sm">
                              {modal.serverData.required_task.description}
                            </div>
                          </div>
                        </>
                      ) : modal.serverData.details ? (
                        <p className="text-gray-300 text-sm">
                          {modal.serverData.details}
                        </p>
                      ) : modal.message === "Task requirements not met" ? (
                        <p className="text-yellow-400 text-sm">
                          You must complete your required task before claiming.
                          Please complete your task and try again later.
                        </p>
                      ) : (
                        <p className="text-gray-500 text-sm italic">
                          No additional details provided.
                        </p>
                      )}
                    </div>
                  </div>
                )}

              {/* Action Button */}
              {!modal.processing && (
                <button
                  className={`w-full py-3 px-6 rounded-lg font-bold gothamMedium transition-all duration-200 ${
                    modal.success
                      ? "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue hover:opacity-90 text-white"
                      : "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue hover:opacity-90 text-white"
                  }`}
                  onClick={() => setModal({ ...modal, open: false })}
                >
                  {modal.success ? "Awesome!" : "Try Again"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function PaymentsHostingPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, walletAddress, ipAddress } = useAuth();

  // State
  const [product, setProduct] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("crypto");
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedTokenType, setSelectedTokenType] = useState(null);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [clientSecret, setClientSecret] = useState("");
  const [price, setPrice] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const { walletAddress: address } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownStates, setDropdownStates] = useState({
    token: false,
    network: false,
  });
  const [filteredNetworks, setFilteredNetworks] = useState([]);
  const [modalStates, setModalStates] = useState({
    activeModal: null, // Can be: null, 'payment', 'faq'
  });
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState({
    success: null,
    error: null,
  });
  const [loadingStates, setLoadingStates] = useState({
    stripe: false,
    balance: false,
  });
  const [errors, setErrors] = useState({
    stripe: null,
    balance: null,
  });
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [pricingTiersLoading, setPricingTiersLoading] = useState(true);
  const [hostingData, setHostingData] = useState(null);
  const [hostingLoading, setHostingLoading] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [selectedTier, setSelectedTier] = useState(null);
  const [isCryptoSelected, setIsCryptoSelected] = useState(false);
  const [isBalanceFetched, setIsBalanceFetched] = useState(false);
  const [customAmountError, setCustomAmountError] = useState("");
  const [insufficientBalanceError, setInsufficientBalanceError] = useState("");
  const paymentIntentUrl =
    "https://us-central1-prodmxs.cloudfunctions.net/createPaymentIntent";
  const [isApprovalPending, setIsApprovalPending] = useState(false);
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const [paymentTransactionHash, setPaymentTransactionHash] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [mintError, setMintError] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalContent, setPaymentModalContent] = useState({
    title: "",
    message: "",
    status: "processing", // processing, success, error
    details: null,
  });
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [adminWallet, setAdminWallet] = useState("");

  // Find the Basic tier
  const basicTier = pricingTiers.find((tier) => tier.tier_name === "Basic");
  const basicTierMinAmount = 1;

  const calculateCredits = (amount) => {
    // Apply tiered conversion rate based on amount
    if (amount >= 100) {
      return 60 * amount; // $100+ = 60 credits per $1
    } else if (amount >= 50) {
      return 50 * amount; // $50-$99 = 50 credits per $1
    } else if (amount >= 25) {
      return 40 * amount; // $25-$49 = 40 credits per $1
    } else if (amount >= 5) {
      return 30 * amount; // $5-$24 = 30 credits per $1
    } else {
      return 30 * amount; // Default to lowest tier for amounts under $5
    }
  };

  const handleTierSelection = (tier) => {
    setSelectedTier(tier);
    setCustomAmount(null); // Clear custom amount when a tier is selected
  };

  // Memoized data processors
  const processSupportedTokens = useCallback((products_currencies) => {
    return products_currencies?.length ? [...products_currencies] : [];
  }, []);

  const processPaymentMethods = (payment_methods) => {
    if (!payment_methods) return [];

    const isDevelopment =
      process.env.NEXT_PUBLIC_PAYMENT_METHOD === "development" ||
      window.location.hostname === "localhost";

    return payment_methods
      .filter((method) => {
        const isGlobalConfigValid = isDevelopment
          ? method.global_config.is_development
          : method.global_config.is_production;
        const isProductConfigValid = isDevelopment
          ? method.product_config.is_development
          : method.product_config.is_production;
        return isGlobalConfigValid && isProductConfigValid;
      })
      .map((method) => method.method_name);
  };

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const { loadStripe } = await import("@stripe/stripe-js");
        const stripe = await loadStripe(
          process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
        );
        setStripePromise(stripe);
      } catch (err) {
        console.error("Failed to initialize payment gateway", err);
      }
    };
    initializeStripe();
  }, []);

  // Fetch product data and admin wallet together
  useEffect(() => {
    const fetchAdminWallet = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.adminWallet, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
          },
        });
        const result = await response.json();
        if (result.success && result.data && result.data.wallet_address) {
          setAdminWallet(result.data.wallet_address);
        } else if (
          result.data &&
          Array.isArray(result.data) &&
          result.data[0]?.wallet_address
        ) {
          setAdminWallet(result.data[0].wallet_address);
        } else {
          throw new Error(result.error || "Failed to fetch admin wallet");
        }
      } catch (error) {
        console.error("Error fetching admin wallet:", error);
      }
    };

    const fetchData = async () => {
      const pid = searchParams.get("pid");
      if (!pid) {
        setIsLoading(false);
        return;
      }

      // Fetch admin wallet and product data in parallel
      await Promise.all([
        fetchAdminWallet(),
        (async () => {
          // Check cache first
          const productsData = sessionStorage.getItem("productsData");
          const cacheTimestamp = sessionStorage.getItem("productsTimestamp");
          if (
            productsData &&
            cacheTimestamp &&
            Date.now() - cacheTimestamp < CACHE_EXPIRY
          ) {
            const parsedData = JSON.parse(productsData);
            const matchingProduct = parsedData.find(
              (p) => p.pid === Number(pid)
            );
            if (matchingProduct) {
              updateProductState(matchingProduct);
              return;
            }
          }
          await fetchProduct(pid);
        })(),
      ]);
    };

    fetchData();
  }, [searchParams]);

  // Fetch node hosting data
  useEffect(() => {
    const fetchHostingData = async () => {
      setHostingLoading(true);
      try {
        const hostingFeesData = await fetchNodeHostingData(address);
        if (hostingFeesData) {
          setHostingData(hostingFeesData.data || null);
        }
      } catch (error) {
        console.error("Error fetching node hosting data:", error);
      } finally {
        setHostingLoading(false);
      }
    };

    if (address) {
      fetchHostingData();
    }
  }, [address]);

  const updateProductState = (productData) => {
    setProduct(productData);
    setAvailableTokens(processSupportedTokens(productData.products_currencies));
    setAvailablePaymentMethods(
      processPaymentMethods(productData.payment_methods)
    );
    if (productData.price !== undefined) setPrice(productData.price);
    setIsLoading(false);
  };

  const fetchProduct = async (pid) => {
    try {
      setIsLoading(true);
      const token = generateJWTToken();
      const response = await fetch(API_ENDPOINTS.products, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ platform: "website" }),
      });

      const result = await response.json();

      if (result.success) {
        const matchingProduct = result.data.find((p) => p.pid === Number(pid));
        if (matchingProduct) {
          updateProductState(matchingProduct);
          sessionStorage.setItem("productsData", JSON.stringify(result.data));
          sessionStorage.setItem("productsTimestamp", Date.now().toString());
        }
      } else {
        console.error("Error fetching Product data:", result.error);
      }
    } catch (error) {
      console.error("Error fetching Product data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Function to call the backend Cloud Function ---
  const createPaymentIntentOnBackend = async (amount) => {
    const amountInCents = Math.round(amount * 100); // Convert dollars to cents
    // console.log(
    //   `Calling backend to create PaymentIntent for amount: $${amount} (${amountInCents} cents)`
    // );

    if (amountInCents < 50) {
      // Stripe minimum charge is typically $0.50
      toast.error("Amount must be at least $0.50 for card payments.");
      console.error("Attempted to create payment intent below Stripe minimum.");
      return null;
    }

    try {
      const response = await fetch(paymentIntentUrl, {
        // Use the defined URL
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add any other required headers like Authorization if needed
        },
        body: JSON.stringify({ amount: amountInCents }), // Send amount in cents
      });

      if (!response.ok) {
        // Handle non-2xx responses
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to parse error response" })); // Try to parse error
        console.error(
          "Error response from backend:",
          response.status,
          errorData
        );
        toast.error(
          `Error creating payment intent: ${
            errorData.error || response.statusText
          }`
        );
        return null;
      }

      const data = await response.json();

      if (data.clientSecret) {
        // console.log("Received clientSecret:", data.clientSecret);
        return data.clientSecret;
      } else {
        console.error("Backend response OK, but missing clientSecret:", data);
        toast.error(
          "Could not initialize payment. Invalid response from server."
        );
        return null;
      }
    } catch (error) {
      console.error("Error fetching createPaymentIntent:", error);
      toast.error(
        "Failed to connect to payment service. Please check console."
      );
      return null;
    }
  };
  // --- End Backend Call Function ---

  const handleTokenSelection = async (token) => {
    setSelectedToken(token);
    // console.log("Selected token:", token);
    setDropdownStates((prev) => ({ ...prev, token: false }));

    if (token && walletAddress) {
      setLoadingStates((prev) => ({ ...prev, balance: true }));
      setErrors((prev) => ({ ...prev, balance: null }));

      try {
        const switchSuccess = await switchNetwork(token);
        if (switchSuccess) await fetchWalletBalance(token.token_address);
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          balance: `Error selecting token: ${error.message}`,
        }));
      } finally {
        setLoadingStates((prev) => ({ ...prev, balance: false }));
      }
    }
  };

  const fetchWalletBalance = async (tokenAddress) => {
    if (!walletAddress || !tokenAddress) {
      setErrors((prev) => ({
        ...prev,
        balance: "Missing required information",
      }));
      return;
    }

    let retries = 3;
    let lastError = null;

    while (retries > 0) {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const contract = new ethers.Contract(tokenAddress, erc20Abi, provider);

        // Verify we're on the correct network
        const network = await provider.getNetwork();
        const token = availableTokens.find(
          (t) => t.token_address === tokenAddress
        );

        if (network.chainId !== token.chain_id) {
          throw new Error("Incorrect network detected");
        }

        const [balance, tokenDecimals] = await Promise.all([
          contract.balanceOf(walletAddress),
          contract.decimals(),
        ]);

        setWalletBalance(
          parseFloat(ethers.utils.formatUnits(balance, tokenDecimals))
        );
        return; // Success, exit the retry loop
      } catch (error) {
        console.error(`Attempt ${4 - retries} failed:`, error);
        lastError = error;
        retries--;

        if (retries > 0) {
          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }

    // If we get here, all retries failed
    console.error(
      "Error fetching wallet balance after all retries:",
      lastError
    );
    setErrors((prev) => ({
      ...prev,
      balance: `Failed to fetch balance: ${lastError.message}`,
    }));
    setWalletBalance(null);
  };

  const getNetworkData = async (token) => {
    try {
      const { data: networkData, error } = await supabase
        .from("token_networks")
        .select("*")
        .eq("chain_id", token.chain_id)
        .single();

      if (error)
        throw new Error(`Error fetching network data: ${error.message}`);
      if (!networkData)
        throw new Error(
          `No network configuration found for chain ID ${token.chain_id}`
        );

      return {
        chainId: `0x${token.chain_id.toString(16)}`,
        chainName: networkData.chain_name,
        nativeCurrency: {
          name: networkData.native_currency_name,
          symbol: networkData.native_currency_symbol,
          decimals: networkData.native_currency_decimals,
        },
        rpcUrls: [networkData.rpc_url],
        blockExplorerUrls: [networkData.block_explorer_url],
      };
    } catch (error) {
      console.error("Error in getNetworkData:", error);
      throw error;
    }
  };

  const switchNetwork = async (token) => {
    if (!token || !window.ethereum) {
      toast.error("Please install MetaMask to continue");
      return false;
    }

    const chainId = token.chain_id;
    const chainIdHex = `0x${chainId.toString(16)}`;

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const network = await provider.getNetwork();

      if (network.chainId !== chainId) {
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: chainIdHex }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            const networkData = await getNetworkData(token);
            await window.ethereum.request({
              method: "wallet_addEthereumChain",
              params: [networkData],
            });
          } else {
            throw switchError;
          }
        }

        // Wait for network change to be reflected and verify
        let retries = 3;
        let success = false;

        while (retries > 0 && !success) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const updatedProvider = new ethers.providers.Web3Provider(
            window.ethereum
          );
          const updatedNetwork = await updatedProvider.getNetwork();

          if (updatedNetwork.chainId === chainId) {
            success = true;
          } else {
            retries--;
            if (retries === 0) {
              throw new Error("Network switch failed after multiple attempts");
            }
          }
        }
      }
      return true;
    } catch (error) {
      console.error("Network switch error:", error);
      setErrors((prev) => ({
        ...prev,
        balance: `Network switch error: ${error.message}`,
      }));
      return false;
    }
  };

  // Fetch conversion rates
  useEffect(() => {
    const fetchConversionRates = async () => {
      setPricingTiersLoading(true);
      try {
        const response = await fetch(API_ENDPOINTS.conversionRate, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
          },
        });
        const result = await response.json();
        if (result.success) {
          const activeTiers = result.data.filter((tier) => tier.active);
          setPricingTiers(activeTiers);
        } else {
          console.error("Failed to fetch conversion rates:", result.error);
        }
        setPricingTiersLoading(false);
      } catch (error) {
        console.error("Error fetching conversion rates:", error);
        setPricingTiersLoading(false);
      }
    };

    fetchConversionRates();
  }, []);

  // Function to handle payment method selection
  const handlePaymentMethodSelection = (method) => {
    setPaymentMethod(method);
    setIsCryptoSelected(method === "crypto");
    // Reset crypto-specific states if switching away
    if (method !== "crypto") {
      setIsBalanceFetched(false);
      setSelectedTokenType(null);
      setSelectedToken(null);
    }
  };

  // Effect to fetch balance when token and network are selected
  useEffect(() => {
    if (paymentMethod === "crypto" && selectedTokenType && selectedToken) {
      setIsBalanceFetched(false); // Reset fetch status before new fetch
      fetchBalance();
    }
  }, [paymentMethod, selectedTokenType, selectedToken]);

  const fetchBalance = async () => {
    // console.log("Fetching balance...");
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate delay
    setIsBalanceFetched(true);
    // console.log("Balance fetched.");
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    // Check for decimal input
    if (value.includes(".")) {
      setCustomAmountError(
        "Decimal values are not allowed. Please enter a whole number."
      );
      return;
    }
    // Only allow whole numbers and enforce a minimum of 1
    if (value === "" || (/^\d+$/.test(value) && Number(value) >= 1)) {
      const numValue = value === "" ? null : Number(value);
      setCustomAmount(value); // Store the raw string value for the input field
      setSelectedTier(null); // Clear selected tier

      // Validation
      if (numValue !== null && numValue < basicTierMinAmount) {
        setCustomAmountError(`Minimum amount must be $${basicTierMinAmount}.`);
      } else {
        setCustomAmountError(""); // Clear error if valid or empty
      }
    } else {
      setCustomAmountError("Amount must be at least $1.");
    }
  };

  // Render helpers
  const renderPaymentMethodOption = (method) => (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
        paymentMethod === method
          ? "border-metaxseed-blue bg-[#1a2d41]"
          : "border-[#3a3a3c] hover:border-gray-500"
      }`}
      onClick={() => {
        handlePaymentMethodSelection(method);
        if (method === "stripe") {
          setPaymentStatus({ success: null, error: null });
          setModalStates({ activeModal: "payment" });
        }
      }}
    >
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 rounded-full border-2 border-[#3a3a3c] flex items-center justify-center">
          {paymentMethod === method && (
            <div className="w-3 h-3 bg-metaxseed-open-blue rounded-full"></div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <img
            src={PAYMENT_METHODS[method].icon}
            alt={method}
            className="w-7 h-7"
          />
          <span className="text-white font-medium text-sm gothamMedium">
            {PAYMENT_METHODS[method].label}
          </span>
        </div>
      </div>
    </div>
  );

  const renderDropdownItem = (item, isNetwork = false) => {
    const itemData = isNetwork
      ? {
          logo: item.network_logo_url,
          name: item.network,
        }
      : {
          logo: item.logo_url,
          name: item.symbol,
        };

    return (
      <div
        className="py-2 px-4 hover:bg-gray-700 cursor-pointer flex items-center gap-4"
        onClick={() => {
          if (isNetwork) {
            handleTokenSelection(item);
            setDropdownStates((prev) => ({ ...prev, network: false }));
          } else {
            setSelectedTokenType(item);
            setDropdownStates((prev) => ({ ...prev, token: false }));
            const networks = availableTokens.filter(
              (t) => t.symbol === item.symbol
            );
            if (networks.length === 1) {
              handleTokenSelection(networks[0]);
            } else {
              setDropdownStates((prev) => ({ ...prev, network: true }));
              setFilteredNetworks(networks);
            }
          }
        }}
      >
        {itemData.logo && (
          <img
            src={itemData.logo}
            alt={itemData.name}
            className="w-6 h-6"
            onError={(e) => (e.target.style.display = "none")}
          />
        )}
        <span className="text-white font-medium text-sm">{itemData.name}</span>
      </div>
    );
  };

  // Add this useEffect to pre-process tokens and ensure they're ready
  useEffect(() => {
    if (availableTokens.length > 0) {
      // Pre-process tokens to ensure they're ready for display
      const uniqueSymbols = Array.from(
        new Set(
          availableTokens.filter((t) => t && t.symbol).map((t) => t.symbol)
        )
      );

      // console.log(
      //   `Pre-processed ${uniqueSymbols.length} unique tokens for dropdown`
      // );
    }
  }, [availableTokens]);

  // Modify the renderTokenSelection function for more reliable dropdown
  const renderTokenSelection = () => {
    const uniqueSymbols = Array.from(
      new Set(availableTokens.filter((t) => t && t.symbol).map((t) => t.symbol))
    );

    return (
      <div className="relative">
        <div
          className="py-3 px-4 bg-metaxseed-dark-gray rounded-lg border border-gray-600 flex items-center justify-between cursor-pointer"
          onClick={() => {
            // Force the dropdown to show, with a small delay
            setDropdownStates((prev) => ({
              ...prev,
              token: false,
              network: false,
            }));

            // Force re-render after a small delay
            setTimeout(() => {
              setDropdownStates((prev) => ({
                ...prev,
                token: true,
                network: false,
              }));
            }, 50);
          }}
        >
          {selectedTokenType ? (
            <>
              <div className="flex items-center gap-4">
                {selectedTokenType?.logo_url && (
                  <img
                    src={selectedTokenType.logo_url}
                    alt="Token"
                    className="w-6 h-6"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                )}
                <span className="text-white font-medium text-sm">
                  {selectedTokenType.symbol}
                </span>
              </div>
              <Icon
                icon="material-symbols:keyboard-arrow-down-rounded"
                className={`w-6 h-6 transition-transform ${
                  dropdownStates.token ? "rotate-180" : ""
                }`}
                width="24px"
              />
            </>
          ) : (
            <>
              <span className="text-gray-400">Select token</span>
              <Icon
                icon="material-symbols:keyboard-arrow-down-rounded"
                className={`w-6 h-6 transition-transform ${
                  dropdownStates.token ? "rotate-180" : ""
                }`}
                width="24px"
              />
            </>
          )}
        </div>
        {dropdownStates.token && (
          <div
            className="absolute z-50 mt-2 w-full bg-metaxseed-dark-gray rounded-lg border border-gray-600 overflow-y-auto"
            style={{
              maxHeight: "240px",
              minHeight: "120px",
            }}
          >
            {uniqueSymbols.length === 0 ? (
              <div className="py-2 px-4 text-center text-gray-400">
                Loading tokens...
              </div>
            ) : (
              uniqueSymbols.map((symbol) => {
                const tokenType = availableTokens.find(
                  (t) => t.symbol === symbol
                );
                return tokenType ? renderDropdownItem(tokenType) : null;
              })
            )}
          </div>
        )}
      </div>
    );
  };

  // Update the renderNetworkSelection function to have proper styling
  const renderNetworkSelection = () => (
    <div className="relative">
      <div
        className="py-3 px-4  bg-metaxseed-dark-gray rounded-lg border border-gray-600 flex items-center justify-between cursor-pointer"
        onClick={() => {
          // Force the dropdown to show properly
          setDropdownStates((prev) => ({
            ...prev,
            network: !prev.network,
          }));
        }}
      >
        {selectedToken ? (
          <>
            <div className="flex items-center gap-4">
              {selectedToken.network_logo_url && (
                <img
                  src={selectedToken.network_logo_url}
                  alt="Network"
                  className="w-6 h-6"
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
              <span className="text-white font-medium text-sm">
                {selectedToken.network}
              </span>
            </div>
            <Icon
              icon="material-symbols:keyboard-arrow-down-rounded"
              className={`w-6 h-6 transition-transform ${
                dropdownStates.network ? "rotate-180" : ""
              }`}
              width="24px"
            />
          </>
        ) : (
          <>
            <span className="text-gray-400">Select network</span>
            <Icon
              icon="material-symbols:keyboard-arrow-down-rounded"
              className={`w-6 h-6 transition-transform ${
                dropdownStates.network ? "rotate-180" : ""
              }`}
              width="24px"
            />
          </>
        )}
      </div>
      {dropdownStates.network && (
        <div className="absolute z-[100] mt-2 w-full bg-metaxseed-dark-gray rounded-lg border border-gray-600 overflow-y-auto shadow-2xl">
          {filteredNetworks.map((network) => renderDropdownItem(network, true))}
        </div>
      )}
    </div>
  );

  const renderBalanceStatus = () => {
    if (loadingStates.balance) {
      return (
        <div className="flex items-center gap-2">
          <Icon
            icon="eos-icons:loading"
            className="h-4 w-4 text-white"
            width="24px"
          />
          Fetching balance...
        </div>
      );
    }
    if (errors.balance) {
      return <span className="text-red-400">Error fetching balance</span>;
    }
    if (walletBalance !== null) {
      return (
        <span className="text-green-300">
          Balance: {walletBalance.toFixed(2)} {selectedToken.symbol}
        </span>
      );
    }
    return null;
  };

  useEffect(() => {
    // console.log("Debug payment state:", {
    //   clientSecret: !!clientSecret,
    //   stripePromise: !!stripePromise,
    //   paymentMethod,
    // });
  }, [clientSecret, stripePromise, paymentMethod]);

  // Update the confirmPaymentWithBackend function
  const confirmPaymentWithBackend = async (paymentData) => {
    try {
      // Add card details to the payment data
      const enrichedPaymentData = {
        ...paymentData,
        card_brand: paymentData.payment_method_details?.card?.brand || null,
        card_last4: paymentData.payment_method_details?.card?.last4 || null,
        card_expiry: paymentData.payment_method_details?.card
          ? `${paymentData.payment_method_details.card.exp_month}/${paymentData.payment_method_details.card.exp_year}`
          : null,
        device_fingerprint:
          paymentData.payment_method_details?.card?.fingerprint || null,
      };

      const response = await fetch(
        "https://us-central1-prodmxs.cloudfunctions.net/payment_confirmation_update",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
          },
          body: JSON.stringify(enrichedPaymentData),
        }
      );

      const result = await response.json();

      if (result.success) {
        setPaymentModalContent({
          title: "Payment Successful!",
          message: "Your payment has been processed successfully.",
          status: "success",
          details: {
            ...result.data,
            audit_log: result.data.audit_log || null,
          },
        });

        // Refresh Node Hosting Data to get the latest data
        fetchFreshHostingData();

        return result;
      } else {
        throw new Error(result.error || "Payment confirmation failed");
      }
    } catch (error) {
      console.error("Payment confirmation error:", error);
      setPaymentModalContent({
        title: "Payment Failed",
        message: error.message || "Failed to confirm payment with the server.",
        status: "error",
        details: null,
      });
      throw error;
    }
  };

  // Function to fetch fresh hosting data
  const fetchFreshHostingData = async () => {
    try {
      const hostingFeesData = await fetchNodeHostingData(address);
      if (hostingFeesData) {
        setHostingData(hostingFeesData.data || null);
      }
    } catch (error) {
      console.error("Error refreshing hosting data:", error);
    }
  };

  const checkSufficientBalance = () => {
    if (!walletBalance || !selectedToken) return false;

    // Calculate required amount based on selected tier or custom amount
    const usdAmount = selectedTier
      ? selectedTier.min_usd_amount
      : Number(customAmount);

    // Check if wallet balance is sufficient
    if (walletBalance < usdAmount) {
      setInsufficientBalanceError(
        `Insufficient balance. You need at least ${usdAmount} ${
          selectedToken.symbol
        } but you only have ${walletBalance.toFixed(2)} ${
          selectedToken.symbol
        }.`
      );
      return false;
    }

    // Clear error if balance is sufficient
    setInsufficientBalanceError("");
    return true;
  };

  // Update the handlePaymentSubmit function
  const handlePaymentSubmit = async () => {
    // Close the payment method modal
    setModalStates({ activeModal: null });

    // Show the payment modal
    setShowPaymentModal(true);

    if (paymentMethod === "crypto") {
      // Check for sufficient balance
      if (!checkSufficientBalance()) {
        setShowPaymentModal(false);
        toast.error(insufficientBalanceError);
        return;
      }

      try {
        setPaymentModalContent({
          title: "Processing Crypto Payment",
          message: "Please confirm the transaction in your wallet...",
          status: "processing",
          details: null,
        });

        // Calculate required token amount based on selected tier or custom amount
        const amount = selectedTier
          ? selectedTier.min_usd_amount
          : Number(customAmount);
        const requiredTokenAmount = ethers.utils.parseUnits(
          amount.toString(),
          selectedToken.decimals
        );
        // Add this line to format the required amount for display or debugging
        const formattedRequiredAmount = parseFloat(
          ethers.utils.formatUnits(requiredTokenAmount, selectedToken.decimals)
        );
        // console.log("Formatted required amount:", formattedRequiredAmount);

        // Handle token payment
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const tokenContract = new ethers.Contract(
          selectedToken.token_address,
          erc20Abi,
          signer
        );

        // Approve spending with explicit gas parameters
        setIsApprovalPending(true);
        try {
          setPaymentModalContent({
            title: "Approving Token Spending",
            message:
              "Please confirm the approval transaction in your wallet...",
            status: "processing",
            details: null,
          });

          // Get the current gas price
          const gasPrice = await provider.getGasPrice();

          // Estimate gas for the approval
          const estimatedGas = await tokenContract.estimateGas
            .approve(adminWallet, requiredTokenAmount)
            .catch(async (error) => {
              // If estimation fails, use a safe default
              console.log("Gas estimation failed, using default:", error);
              return ethers.BigNumber.from("100000"); // Safe default
            });

          // Add 20% buffer to gas limit
          const gasLimit = estimatedGas.mul(120).div(100);

          // Execute the approval transaction
          const approvalTx = await tokenContract
            .approve(adminWallet, requiredTokenAmount, {
              gasLimit: gasLimit,
              gasPrice: gasPrice,
            })
            .catch((error) => {
              // Handle MetaMask rejection
              if (error.code === 4001) {
                // MetaMask user rejected
                setShowPaymentModal(false);
                toast.info("Transaction cancelled by user");
                throw new Error("User cancelled the transaction");
              }
              throw error;
            });

          // console.log("Approval transaction hash:", approvalTx.hash);
          setIsApprovalPending(false);

          setPaymentModalContent({
            title: "Approving Token Spending",
            message: "Waiting for transaction confirmation...",
            status: "processing",
            details: null,
          });

          const receipt = await approvalTx.wait();
          // console.log("Approval transaction receipt:", receipt);
        } catch (error) {
          console.error("Approval failed:", error);
          setIsApprovalPending(false);

          // Check for specific error types
          if (error.message === "User cancelled the transaction") {
            setShowPaymentModal(false);
            return; // Exit early, don't show error modal
          } else if (error.code === -32603) {
            setErrorDetails(
              "Transaction failed due to network conditions. Please try again with adjusted gas settings."
            );
          } else if (error.message?.includes("user rejected")) {
            setShowPaymentModal(false);
            toast.info("Transaction cancelled by user");
            return; // Exit early, don't show error modal
          } else {
            setErrorDetails(
              error.message || "Approval failed. Please try again."
            );
          }
          setMintError(true);
          throw error;
        }

        // Transfer tokens
        setIsPaymentPending(true);
        let txHash;

        try {
          setPaymentModalContent({
            title: "Processing Payment",
            message: "Please confirm the payment transaction in your wallet...",
            status: "processing",
            details: null,
          });

          // Get the current gas price
          const paymentGasPrice = await provider.getGasPrice();

          // Estimate gas for the transfer
          const estimatedTransferGas = await tokenContract.estimateGas
            .transfer(adminWallet, requiredTokenAmount)
            .catch(async (error) => {
              // If estimation failed, use a safe default
              console.log(
                "Transfer gas estimation failed, using default:",
                error
              );
              return ethers.BigNumber.from("100000"); // Safe default
            });

          // Add 20% buffer to gas limit
          const transferGasLimit = estimatedTransferGas.mul(120).div(100);

          // Execute the transfer with explicit gas parameters
          const paymentTransaction = await tokenContract
            .transfer(adminWallet, requiredTokenAmount, {
              gasLimit: transferGasLimit,
              gasPrice: paymentGasPrice,
            })
            .catch((error) => {
              // Handle MetaMask rejection
              if (error.code === 4001) {
                // MetaMask user rejected
                setShowPaymentModal(false);
                toast.info("Transaction cancelled by user");
                throw new Error("User cancelled the transaction");
              }
              throw error;
            });

          // Set the transaction hash immediately after getting the transaction
          txHash = paymentTransaction.hash;
          setPaymentTransactionHash(txHash);
          // console.log("Payment transaction hash:", txHash);

          setIsPaymentPending(false);

          setPaymentModalContent({
            title: "Processing Payment",
            message: "Waiting for transaction confirmation...",
            status: "processing",
            details: null,
          });

          const paymentReceipt = await paymentTransaction.wait();
          // console.log("Payment transaction confirmed:", paymentReceipt);

          // Confirm payment with backend
          setPaymentModalContent({
            title: "Confirming Payment",
            message:
              "Please wait while we confirm your payment with our servers...",
            status: "processing",
            details: null,
          });

          // For crypto payment, use the transaction hash as the payment_id
          // Determine appropriate tier based on custom amount
          let selectedTierForPayment = selectedTier;
          if (!selectedTier && amount) {
            // For custom amounts, set tier to "Custom"
            selectedTierForPayment = {
              tier_name: "Custom",
              id: null,
            };
          }

          const paymentData = {
            payment_id: txHash,
            amount: amount.toString(),
            wallet: walletAddress,
            credits: selectedTier
              ? (
                  selectedTier.base_credit_per_usd * selectedTier.min_usd_amount
                ).toString()
              : Math.floor(calculateCredits(amount)).toString(),
            tier: selectedTierForPayment
              ? selectedTierForPayment.tier_name
              : "Custom",
            tier_id: selectedTierForPayment ? selectedTierForPayment.id : null,
            user_id: user?.uid || "anonymous",
            payment_type: "node_hosting",
            payment_method: "crypto",
            network: selectedToken.network || "ethereum",
            confirmations: 6,
            currency: "USD",
            metadata: {
              token_address: selectedToken.token_address,
              token_symbol: selectedToken.symbol,
              token_decimals: selectedToken.decimals,
            },
          };

          const result = await confirmPaymentWithBackend(paymentData);

          // Close the payment method modal after successful payment
          setModalStates({ activeModal: null });

          // Show success message
          toast.success("Payment successful! Transaction hash: " + txHash);
        } catch (error) {
          console.error("Payment transfer failed:", error);
          setIsPaymentPending(false);

          // Check for specific error types
          if (error.message === "User cancelled the transaction") {
            setShowPaymentModal(false);
            return; // Exit early, don't show error modal
          } else if (error.code === -32603) {
            setErrorDetails(
              "Payment failed due to network conditions. Please try again with adjusted gas settings."
            );
          } else if (error.message?.includes("user rejected")) {
            setShowPaymentModal(false);
            toast.info("Transaction cancelled by user");
            return; // Exit early, don't show error modal
          } else {
            setErrorDetails(
              error.message || "Payment failed. Please try again."
            );
          }
          setMintError(true);
          throw error;
        }
      } catch (error) {
        console.error("Crypto payment failed:", error);
        // Only show error toast if it's not a user cancellation
        if (
          !error.message?.includes("User cancelled") &&
          !error.message?.includes("user rejected")
        ) {
          toast.error(errorDetails || "Payment failed. Please try again.");
        }
      }
    } else if (paymentMethod === "stripe") {
      // For Stripe, we don't need to do anything here as the PaymentForm component
      // will handle the payment submission and call the onSuccess/onError callbacks
      setPaymentModalContent({
        title: "Processing Card Payment",
        message: "Please complete the payment form below...",
        status: "processing",
        details: null,
      });
    }
  };

  // Update the PaymentModal component
  const PaymentModal = ({ isOpen, onClose, content }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999] p-3 backdrop-blur-sm overflow-y-auto">
        <div className="bg-metaxseed-dark-gray rounded-xl overflow-hidden border border-[#3a3a3c] shadow-xl transform transition-all max-w-md w-full max-h-[90vh] my-4 text-sm">
          {/* Header with gradient background */}
          <div
            className={`py-3 px-4 relative flex items-center justify-between ${
              content.status === "success"
                ? "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue"
                : content.status === "error"
                ? "bg-gradient-to-r from-metaxseed-ruby-red to-red-700"
                : "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue"
            }`}
          >
            <h3 className="text-base font-bold text-white gothamMedium">
              {content.title}
            </h3>
            {/* Always show close button in header */}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              aria-label="Close modal"
            >
              <Icon icon="mdi:close" className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 py-4 overflow-y-auto max-h-[calc(90vh-60px)]">
            <div className="flex flex-col items-center">
              {content.status === "processing" && (
                <div className="w-16 h-16 mb-4 relative">
                  <div className="w-full h-full animate-spin">
                    <svg
                      className="w-full h-full text-metaxseed-open-blue"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                </div>
              )}

              {content.status === "success" && (
                <div className="w-16 h-16 mb-4 text-green-500">
                  <Icon icon="mdi:check-circle" className="w-full h-full" />
                </div>
              )}

              {content.status === "error" && (
                <div className="w-16 h-16 mb-4 text-metaxseed-ruby-red">
                  <Icon icon="mdi:close-circle" className="w-full h-full" />
                </div>
              )}

              <p className="text-gray-300 text-center mb-4 text-sm">
                {content.message}
              </p>

              {/* Add transaction hash with link if available */}
              {content.status === "success" &&
                content.details?.payment_id &&
                content.details.payment_id.startsWith("0x") && (
                  <div className="w-full mb-4 text-center">
                    <a
                      href={`https://snowtrace.io/tx/${content.details.payment_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-200 hover:text-white underline break-all"
                    >
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Icon icon="carbon:view" className="w-3 h-3" />
                        <span>View transaction on Snowtrace</span>
                      </div>
                      {content.details.payment_id}
                    </a>
                  </div>
                )}

              {content.details && (
                <div className="bg-[#1a1a1a] rounded-lg w-full mb-4 border border-[#3a3a3c] overflow-hidden text-xs">
                  {/* Details Header - Blue gradient for payment details */}
                  <div className="bg-gradient-to-r from-metaxseed-blue to-blue-500 px-3 py-2 border-b border-[#3a3a3c]">
                    <h4 className="text-white font-medium text-xs gothamMedium">
                      Payment Details
                    </h4>
                  </div>

                  {/* Details Content */}
                  <div className="p-3">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                        <span className="text-gray-400 font-medium">
                          Amount:
                        </span>
                        <span className="text-white font-bold">
                          ${Math.round(content.details.amount)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                        <span className="text-gray-400 font-medium">
                          Credits Added:
                        </span>
                        <span className="text-white font-bold">
                          {Math.round(content.details.credits)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                        <span className="text-gray-400 font-medium">
                          New Balance:
                        </span>
                        <span className="text-white font-bold">
                          {Math.round(content.details.new_balance)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                        <span className="text-gray-400 font-medium">
                          Days Added:
                        </span>
                        <span className="font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
                          {Math.round(content.details.hosting_days_added)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-400 font-medium">
                          New Expiry:
                        </span>
                        <span className="text-white font-bold">
                          {new Date(
                            content.details.new_expiry
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fixed footer with close button - always at bottom */}
              <div className="w-full mt-1">
                {content.status !== "processing" ? (
                  <button
                    onClick={onClose}
                    className={`w-full py-3 rounded-lg font-medium transition-all duration-200 gothamMedium shadow-lg ${
                      content.status === "success"
                        ? "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue text-white"
                        : content.status === "error"
                        ? "bg-gradient-to-r from-metaxseed-ruby-red to-red-700 text-white"
                        : "bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue text-white"
                    }`}
                  >
                    Close
                  </button>
                ) : (
                  <button
                    className="w-full py-3 rounded-lg font-medium gothamMedium bg-gray-600 text-gray-300 cursor-not-allowed shadow-lg"
                    disabled
                  >
                    Please Wait...
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading || !product) {
    return isLoading ? (
      <Loading />
    ) : (
      <div className="min-h-screen bg-[#0F1215] flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-xl font-bold mb-4">No product data found</h1>
          <button
            onClick={() => router.push("/")}
            className="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (!walletAddress) {
    return (
      <div className="skeleton_connect_wallet">
        <div className="Connect_Wallet">Login To Continue</div>
      </div>
    );
  }

  // Ensure hostingData is not null before accessing its properties
  const fullNodes = hostingData ? hostingData.user_full_nodes : 0;
  const fractionalNodes = hostingData ? hostingData.user_fractional_nodes : 0;

  // Calculate My Nodes
  const myNodes = fullNodes + fractionalNodes / 10;

  // Add this function near the top of the component
  const getMembershipLabel = (level) => {
    switch (level) {
      case "Bronze":
        return "SHORT TERM";
      case "Silver":
        return "MEDIUM TERM";
      case "Gold":
        return "POPULAR";
      case "Platinum":
        return "BEST VALUE";
      default:
        return level.toUpperCase();
    }
  };

  // Update the openFaqModal function
  const openFaqModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setModalStates({ activeModal: "faq" });
  };

  return (
    <div className="min-h-screen bg-[#0F1215] xs:pt-0 md:pt-0 lg:pt-20 pb-20 relative">
      {/* Announcement modal overlay (blocks all hosting page interaction) */}
      {/* <div className="absolute top-0 left-0 right-0 z-[99] flex justify-center items-start bg-[#181c23]  min-h-full">
        <div className="bg-[#1A1D24] rounded-xl border border-[#3a3a3c] max-w-lg w-full p-6 relative shadow-2xl  mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue flex items-center justify-center flex-shrink-0">
              <Icon icon="material-symbols:info-outline" className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white gothamMedium">Coming Soon</h2>
          </div>
          <div className="space-y-4">
            <p className="text-gray-300 text-sm leading-relaxed">
              Further details on the hosting will be announced in due course. Please follow our socials to keep up to date.
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <a 
                href="https://twitter.com/metaxseed" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Icon icon="mdi:twitter" className="w-5 h-5" />
                <span className="text-sm font-medium">Twitter</span>
              </a>
              <a 
                href="https://discord.com/invite/metaxseed" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752c4] text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Icon icon="mdi:discord" className="w-5 h-5" />
                <span className="text-sm font-medium">Discord</span>
              </a>
            </div>
          </div>
        </div>
      </div> */}
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-white mb-5">&nbsp;</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="flex flex-col gap-4 lg:col-span-2">
            {/* <div className="lg:col-span-2 space-y-8">
              <div className="bg-[#1A1D24] rounded-xl p-6 border border-[#3a3a3c]">
                <h2 className="text-xl font-bold text-white mb-4 gothamMedium">
                  Select Your Credit Package
                </h2>
                <p className="text-sm gothamBook text-metaxseed-text-gray mb-6">
                  Choose your preferred credit package below. All credits are
                  delivered instantly.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  ... (all package cards, custom, FAQ, Continue to Payment button) ...
                </div>
              </div>
            </div> */}
            {/* New Free Credit Claim Card */}
            <ClaimFreeCreditCard
              walletAddress={walletAddress}
              ipAddress={ipAddress}
            />
            {/* <div className="lg:col-span-2 space-y-8" style={{display: 'none'}}> */}
            <div
              className="lg:col-span-2 space-y-8"
              style={{ display: "none" }}
            >
              <div className="bg-[#1A1D24] rounded-xl p-6 border border-[#3a3a3c]">
                <h2 className="text-xl font-bold text-white mb-4 gothamMedium">
                  Select Your Credit Package
                </h2>
                <p className="text-sm gothamBook text-metaxseed-text-gray mb-6">
                  Choose your preferred credit package below. All credits are
                  delivered instantly.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  {pricingTiersLoading || hostingLoading ? (
                    // Show skeleton loading cards during loading
                    <div className="col-span-2 sm:col-span-2 lg:col-span-5 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonPackageCard key={i} />
                      ))}
                    </div>
                  ) : !hostingData || myNodes === 0 ? (
                    // Show node required message only after loading is done and user has no nodes
                    <div className="col-span-full">
                      <div className="bg-[#1a1a1a] rounded-lg p-6 border border-[#3a3a3c]">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-600/20 flex items-center justify-center flex-shrink-0">
                            <Icon
                              icon="material-symbols:info-outline"
                              className="w-6 h-6 text-yellow-500"
                            />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-white mb-1 gothamMedium">
                              Node Required
                            </h3>
                            <p className="text-gray-400 text-sm">
                              You need to purchase a node license first before
                              buying credits. Credits are used to power your
                              nodes.
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => {
                            router.push("/nodes");
                          }}
                          className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:opacity-90 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                        >
                          <Icon
                            icon="material-symbols:shopping-cart-outline"
                            className="w-5 h-5"
                          />
                          <span className="font-semibold">
                            Buy a Node License First
                          </span>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Show actual pricing tiers when loading is done and user has nodes
                    <>
                      {pricingTiers.map((tier) => {
                        const tierName = tier.tier_name;

                        // Update the header color mapping with gradients
                        let headerColor;
                        if (tier.membership_level === "SHORT TERM") {
                          headerColor = "bg-[#4B5563]"; // SHORT TERM - keep as is
                        } else if (tier.membership_level === "MEDIUM TERM") {
                          headerColor =
                            "bg-gradient-to-r from-metaxseed-blue to-blue-500"; // MEDIUM TERM - updated to use gradient
                        } else if (tier.membership_level === "POPULAR") {
                          headerColor =
                            "bg-gradient-to-r from-purple-600 to-pink-500"; // POPULAR - updated to use gradient
                        } else if (tier.membership_level === "BEST VALUE") {
                          headerColor =
                            "bg-gradient-to-r from-yellow-500 to-orange-500"; // BEST VALUE - updated to use gradient
                        } else {
                          headerColor = "bg-[#4B5563]"; // Default - keep as is
                        }

                        const credits = Math.round(
                          calculateCredits(tier.min_usd_amount)
                        );
                        const pricePerCredit = (
                          tier.min_usd_amount / credits
                        ).toFixed(3);

                        return (
                          <div
                            key={tier.id}
                            className={`rounded-lg overflow-hidden border ${
                              selectedTier?.id === tier.id
                                ? "border-metaxseed-blue"
                                : "border-[#3a3a3c]"
                            } ${
                              isApprovalPending || isPaymentPending
                                ? "opacity-60 pointer-events-none"
                                : ""
                            }`}
                            onClick={() =>
                              !isApprovalPending &&
                              !isPaymentPending &&
                              handleTierSelection(tier)
                            }
                          >
                            <div
                              className={`py-2 font-medium text-white text-sm ${headerColor} text-center`}
                            >
                              {getMembershipLabel(tier.membership_level)}
                            </div>
                            <div className="p-4 text-center bg-[#1a1a1a]">
                              <div className="text-lg font-medium text-white mb-1">
                                {tierName}
                              </div>
                              <div className="text-3xl font-bold text-white mb-2">
                                ${Math.floor(tier.min_usd_amount)}
                              </div>
                              <div className="text-gray-400 text-xs mb-4">
                                ${pricePerCredit} per credit
                              </div>
                              <div className="text-metaxseed-open-blue font-semibold text-base mb-4">
                                {Math.floor(credits)} credits
                              </div>
                              <button
                                className={`w-full py-2 px-4 rounded text-white text-sm font-medium ${
                                  selectedTier?.id === tier.id
                                    ? `bg-gradient-to-r from-metaxseed-blue to-blue-500`
                                    : "bg-gray-600 hover:bg-gray-700"
                                }`}
                                disabled={isApprovalPending || isPaymentPending}
                              >
                                {selectedTier?.id === tier.id
                                  ? "Selected"
                                  : "Select"}
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Custom Amount Box */}
                      <div
                        className={`col-span-2 sm:col-span-2 lg:col-span-1 rounded-lg overflow-hidden border ${
                          selectedTier === null && customAmount
                            ? "border-metaxseed-blue"
                            : "border-[#3a3a3c]"
                        }`}
                      >
                        <div className="py-2 font-medium text-white text-sm bg-[#4B5563] text-center">
                          CUSTOM
                        </div>
                        <div className="p-4 text-center bg-[#1a1a1a]">
                          <div className="text-lg font-medium text-white mb-1">
                            Custom
                          </div>
                          <div className="mb-2">
                            <input
                              id="customAmount"
                              type="number"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              min={basicTierMinAmount}
                              step="1"
                              value={customAmount}
                              onChange={handleCustomAmountChange}
                              onKeyPress={(e) => {
                                // Allow only numeric keys
                                const charCode = e.which ? e.which : e.keyCode;
                                if (
                                  charCode > 31 &&
                                  (charCode < 48 || charCode > 57)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              className={`bg-[#252525] text-white p-2 rounded-lg w-full border ${
                                customAmountError
                                  ? "border-metaxseed-ruby-red"
                                  : "border-[#3a3a3c]"
                              } focus:ring-metaxseed-open-blue focus:border-metaxseed-open-blue text-xl`}
                              placeholder={`$${basicTierMinAmount} min`}
                              disabled={isApprovalPending || isPaymentPending}
                            />
                          </div>
                          <div className="text-gray-400 text-xs mb-4">
                            Flexible amount
                          </div>
                          <div
                            className="text-metaxseed-open-blue font-semibold text-base"
                            style={{ minHeight: "24px", marginBottom: "8px" }}
                          >
                            {customAmount && !customAmountError
                              ? `${Math.floor(
                                  calculateCredits(Number(customAmount))
                                )} credits`
                              : ""}
                          </div>
                          {customAmountError && (
                            <div className="text-metaxseed-ruby-red text-xs mb-2">
                              {customAmountError}
                            </div>
                          )}
                          <button
                            className={`w-full py-2 px-4 rounded text-white text-sm font-medium ${
                              selectedTier === null &&
                              customAmount &&
                              !customAmountError
                                ? "bg-gradient-to-r from-metaxseed-blue to-blue-500"
                                : "bg-gray-600 hover:bg-gray-700"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                customAmount &&
                                !customAmountError &&
                                !isApprovalPending &&
                                !isPaymentPending
                              ) {
                                setSelectedTier(null);
                              }
                            }}
                            disabled={isApprovalPending || isPaymentPending}
                          >
                            {selectedTier === null &&
                            customAmount &&
                            !customAmountError
                              ? "Selected"
                              : "Select"}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="text-center mb-6">
                  <button
                    onClick={openFaqModal}
                    className="text-metaxseed-open-blue hover:text-metaxseed-blue text-xs underline flex items-center gap-1 mx-auto bg-[#1a1a1a] px-3 py-1.5 rounded-lg border border-[#3a3a3c] hover:bg-[#252525] transition-colors"
                  >
                    <Icon
                      icon="material-symbols:help-outline"
                      className="w-4 h-4 text-metaxseed-open-blue"
                    />
                    <span className="text-white">
                      How many credits are needed?
                    </span>
                  </button>
                </div>

                {/* Only show summary section if user has nodes */}
                {hostingData &&
                  myNodes > 0 &&
                  (selectedTier ||
                    Number(customAmount) >= basicTierMinAmount) && (
                    <div className="mb-6 border border-[#3a3a3c] rounded-lg overflow-hidden">
                      <div className="py-2 text-center font-medium text-white text-sm bg-gradient-to-r from-metaxseed-blue to-blue-500">
                        SUMMARY
                      </div>
                      <div className="p-4 bg-[#1a1a1a]">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                            <span className="text-gray-400 font-medium text-sm">
                              Selected Amount:
                            </span>
                            <span className="text-white font-semibold text-sm">
                              $
                              {selectedTier
                                ? Math.floor(selectedTier.min_usd_amount)
                                : Math.floor(Number(customAmount))}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                            <span className="text-gray-400 font-medium text-sm">
                              Credits You Get:
                            </span>
                            <span className="text-white font-semibold text-sm">
                              {selectedTier
                                ? Math.floor(
                                    selectedTier.base_credit_per_usd *
                                      selectedTier.min_usd_amount
                                  )
                                : Math.floor(
                                    calculateCredits(Number(customAmount))
                                  )}{" "}
                              Credits
                            </span>
                          </div>
                          {hostingData && (
                            <div className="flex justify-between items-center py-1 border-b border-[#3a3a3c]">
                              <span className="text-gray-400 font-medium text-sm">
                                Days To Be Added:
                              </span>
                              <span className="text-white font-semibold text-sm">
                                {(() => {
                                  const totalCredits = selectedTier
                                    ? Math.floor(
                                        selectedTier.base_credit_per_usd *
                                          selectedTier.min_usd_amount
                                      )
                                    : Math.floor(
                                        calculateCredits(Number(customAmount))
                                      );
                                  const creditPerDay = Math.max(
                                    1,
                                    hostingData.hosting_fee_per_day
                                  );
                                  return Math.floor(
                                    totalCredits / creditPerDay
                                  );
                                })()}{" "}
                                Days
                              </span>
                            </div>
                          )}
                          {!selectedTier && customAmount && (
                            <div className="flex justify-between items-center py-1">
                              <span className="text-gray-400 font-medium text-sm">
                                Conversion Rate:
                              </span>
                              <span className="text-white font-semibold text-sm">
                                {Number(customAmount) >= 100
                                  ? "60 credits per $1"
                                  : Number(customAmount) >= 50
                                  ? "50 credits per $1"
                                  : Number(customAmount) >= 25
                                  ? "40 credits per $1"
                                  : "30 credits per $1"}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                <div className="flex justify-end">
                  {!(pricingTiersLoading || hostingLoading) && (
                    <Button
                      onClick={() => {
                        // If no nodes, redirect to nodes page
                        if (!hostingData || myNodes === 0) {
                          router.push("/nodes");
                          return;
                        }
                        // Existing logic for users with nodes
                        const finalAmount = selectedTier
                          ? selectedTier.min_usd_amount
                          : Number(customAmount);
                        if (
                          !selectedTier &&
                          (finalAmount === null ||
                            finalAmount < basicTierMinAmount)
                        ) {
                          toast.error(
                            `Please select a valid tier or enter an amount of at least $${basicTierMinAmount}.`
                          );
                          setCustomAmountError(
                            `Minimum amount must be $${basicTierMinAmount}.`
                          );
                          return;
                        }
                        if (customAmountError) {
                          toast.error(customAmountError);
                          return;
                        }
                        // Open payment method modal
                        setModalStates({ activeModal: "payment" });
                      }}
                      className={
                        !hostingData || myNodes === 0
                          ? "bg-gradient-to-r from-yellow-500 to-yellow-600 hover:opacity-90 text-white font-medium px-8 py-3 rounded-lg transition-colors"
                          : (!selectedTier &&
                              (Number(customAmount) < basicTierMinAmount ||
                                customAmount === "")) ||
                            !!customAmountError ||
                            isApprovalPending ||
                            isPaymentPending
                          ? "bg-gray-500 cursor-not-allowed text-white font-medium px-8 py-3 rounded-lg transition-colors"
                          : "bg-gradient-to-r from-metaxseed-blue to-blue-500 hover:opacity-90 text-white font-medium px-8 py-3 rounded-lg transition-colors"
                      }
                      disabled={
                        hostingData &&
                        myNodes > 0 &&
                        ((!selectedTier &&
                          (Number(customAmount) < basicTierMinAmount ||
                            customAmount === "")) ||
                          !!customAmountError ||
                          isApprovalPending ||
                          isPaymentPending)
                      }
                    >
                      {!hostingData || myNodes === 0
                        ? "Buy a Node License First"
                        : "Continue to Payment"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Node Hosting Data Section */}
          <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#3a3a3c]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white gothamMedium">
                Node Hosting Data
              </h2>
            </div>
            {hostingLoading ? (
              <SkeletonHostingData />
            ) : !hostingData || myNodes === 0 ? (
              <div className="space-y-6">
                {/* Empty state - No nodes found */}
                <div className="bg-[#252525] rounded-lg p-6 border border-[#3a3a3c] text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue flex items-center justify-center">
                      <Icon
                        icon="material-symbols:cloud-off"
                        className="w-10 h-10 text-white"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 gothamMedium">
                    No Nodes Found
                  </h3>
                  <p className="text-gray-400 text-sm mb-6">
                    You don't have any active nodes in your account yet.
                    Purchase a node to start earning rewards.
                  </p>

                  {/* Buy Node Card */}
                  <div className="bg-[#1a2d41] rounded-lg p-6 border border-metaxseed-blue mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-white gothamMedium">
                        Get Started with Nodes
                      </h4>
                      <div className="w-10 h-10 rounded-full bg-metaxseed-open-blue flex items-center justify-center">
                        <Icon
                          icon="material-symbols:add"
                          className="w-6 h-6 text-white"
                        />
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm mb-4">
                      Nodes allow you to earn daily XSEED rewards while
                      supporting the network.
                    </p>
                    <ul className="mb-6 space-y-2">
                      <li className="flex items-center text-sm text-gray-300">
                        <Icon
                          icon="material-symbols:check-circle"
                          className="w-5 h-5 text-green-500 mr-2"
                        />
                        <span>Daily XSEED rewards</span>
                      </li>
                      <li className="flex items-center text-sm text-gray-300">
                        <Icon
                          icon="material-symbols:check-circle"
                          className="w-5 h-5 text-green-500 mr-2"
                        />
                        <span>Cloud-hosted infrastructure</span>
                      </li>
                      <li className="flex items-center text-sm text-gray-300">
                        <Icon
                          icon="material-symbols:check-circle"
                          className="w-5 h-5 text-green-500 mr-2"
                        />
                        <span>No technical knowledge required</span>
                      </li>
                    </ul>
                    <Button
                      onClick={() => {
                        router.push("/nodes");
                      }}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:opacity-90 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                    >
                      <Icon
                        icon="material-symbols:shopping-cart-outline"
                        className="w-5 h-5"
                      />
                      <span className="font-semibold">Buy a Node License</span>
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500">
                    Note: You'll need to purchase credits to power your nodes
                    after buying a license.
                  </p>
                </div>
              </div>
            ) : hostingData ? (
              <div className="space-y-6">
                {/* First Box: Nodes and Expiry */}
                <div className="bg-[#252525] rounded-lg p-4 border border-[#3a3a3c]">
                  <h3 className="text-metaxseed-open-blue text-sm font-medium mb-4 gothamMedium">
                    Nodes & Expiry Status
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">My Nodes</div>
                      <div className="text-2xl font-bold text-white">
                        {myNodes}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">
                        Hosting Expiry
                      </div>
                      <div className="text-lg font-medium text-white">
                        {hostingData.hosting_expiry
                          ? new Date(
                              hostingData.hosting_expiry
                            ).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">Days Left</div>
                      <div className="text-2xl font-bold text-white">
                        {hostingData.hosting_days_left !== undefined
                          ? Math.round(hostingData.hosting_days_left)
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">
                        Credit Per Day
                      </div>
                      <div className="text-lg font-medium text-white">
                        {Math.floor(hostingData.hosting_fee_per_day)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Second Box: Credits Information */}
                <div className="bg-[#252525] rounded-lg p-4 border border-[#3a3a3c]">
                  <h3 className="text-metaxseed-open-blue text-sm font-medium mb-4 gothamMedium">
                    Credits Overview
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">Total Credit</div>
                      <div className="text-2xl font-bold text-white">
                        {Math.floor(hostingData.total_credit)}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">
                        Current Credit
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {Math.floor(hostingData.current_credit)}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">
                        Total Deducted
                      </div>
                      <div className="text-lg font-medium text-white">
                        {Math.floor(hostingData.total_deducted_credit)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Go to rewards button card */}
                <div className="bg-[#252525] rounded-lg p-4 border border-[#3a3a3c]">
                  <h3 className="text-metaxseed-open-blue text-sm font-medium mb-2 gothamMedium">
                    Manage Your Rewards
                  </h3>
                  <p className="text-gray-400 text-xs mb-4">
                    View detailed reward history, transaction details, and your
                    node hosting details.
                  </p>
                  <Button
                    onClick={() => {
                      router.push("/accounts");
                    }}
                    className="w-full bg-[#1a1a1a] px-6 py-2.5 hover:bg-[#222] text-white font-medium !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none transition-all duration-200 rounded-lg flex items-center justify-center gap-2 border border-[#3a3a3c]"
                  >
                    <Icon
                      icon="material-symbols:payments-outline"
                      className="w-5 h-5 text-metaxseed-open-blue"
                    />
                    <span className="text-metaxseed-open-blue font-semibold">
                      Go To Rewards Dashboard
                    </span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-gray-400">No hosting data available.</div>
            )}

            {/* Frequently Asked Questions - Moved inside the Node Hosting Data card */}
            <div className="mt-6">
              <div className="bg-[#252525] rounded-lg border border-[#3a3a3c] overflow-hidden">
                <div className="py-3 px-4 bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue/80">
                  <h3 className="text-white font-medium text-sm flex items-center gap-2 gothamMedium">
                    <Icon
                      icon="material-symbols:help-center-outline"
                      className="w-5 h-5"
                    />
                    Frequently Asked Questions
                  </h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-400 text-xs mb-4">
                    Have questions about the credit system, hosting fees, or
                    reward calculations? Get answers to common questions about
                    how the node hosting system works.
                  </p>
                  <button
                    onClick={openFaqModal}
                    className="w-full bg-[#1a1a1a] hover:bg-[#1a2d41] text-white font-medium py-2 px-4 rounded-md flex items-center justify-center gap-2 border border-[#3a3a3c] transition-all duration-200"
                  >
                    <Icon
                      icon="material-symbols:quiz"
                      className="w-5 h-5 text-metaxseed-open-blue"
                    />
                    <span className="bg-gradient-to-r from-metaxseed-blue to-blue-500 bg-clip-text text-transparent font-semibold">
                      Open Cloud Hosting FAQ
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Remove the standalone FAQ section that was outside the Node Hosting Data card */}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        content={paymentModalContent}
      />

      {/* FAQ Modal using the imported Modal component */}
      <Modal
        activeModal={modalStates.activeModal === "faq"}
        onClose={() => {
          setModalStates({ activeModal: null });
        }}
        centered={true}
        title="Cloud Hosting FAQ"
        className="max-w-2xl bg-metaxseed-dark-gray"
        themeClass="bg-gradient-to-r from-metaxseed-blue to-blue-500 !px-4 !py-3"
        bodyClass="!p-4"
      >
        {renderFAQContent(faqData, activeFaqIndex, setActiveFaqIndex)}
        <div className="flex justify-end mt-4">
          <Button
            onClick={() => {
              setModalStates({ activeModal: null });
            }}
            className="bg-gray-600 hover:bg-gray-700 text-white text-sm font-normal !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none py-1.5 px-3"
          >
            Close
          </Button>
        </div>
      </Modal>

      {/* Payment Method Modal */}
      {modalStates.activeModal === "payment" && (
        <Modal
          activeModal={modalStates.activeModal === "payment"}
          onClose={() => {
            setModalStates({ activeModal: null });
          }}
          centered={true}
          title="Select Payment Method"
          className="max-w-md bg-metaxseed-dark-gray"
          themeClass="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue !px-3 !py-2"
          bodyClass="!p-3"
        >
          <div className="bg-metaxseed-dark-gray">
            <div className="p-4">
              <h2 className="text-lg font-bold text-white mb-4 gothamMedium text-center">
                Complete Your Purchase
              </h2>

              <div className="grid grid-cols-1 gap-4 mb-4">
                {availablePaymentMethods.includes("crypto") &&
                  renderPaymentMethodOption("crypto")}
                {availablePaymentMethods.includes("stripe") &&
                  renderPaymentMethodOption("stripe")}
              </div>

              {paymentMethod === "stripe" && (
                <div className="mt-6">
                  {clientSecret && stripePromise ? (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        appearance: STRIPE_THEME,
                      }}
                    >
                      <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700">
                        <PaymentForm
                          setShowPaymentModal={setShowPaymentModal}
                          setPaymentModalContent={setPaymentModalContent}
                          onSuccess={async (paymentIntent) => {
                            setPaymentStatus({
                              success: "Payment successful!",
                              error: null,
                            });

                            // Show confirming modal
                            setShowPaymentModal(true);
                            setPaymentModalContent({
                              title: "Confirming Payment",
                              message:
                                "Please wait while we confirm your payment with our servers...",
                              status: "processing",
                              details: null,
                            });

                            const amount = selectedTier
                              ? selectedTier.min_usd_amount
                              : Number(customAmount);
                            // Determine appropriate tier based on custom amount
                            let selectedTierForPayment = selectedTier;
                            if (!selectedTier && amount) {
                              // For custom amounts, set tier to "Custom"
                              selectedTierForPayment = {
                                tier_name: "Custom",
                                id: null,
                              };
                            }
                            const paymentData = {
                              payment_id: paymentIntent.id,
                              amount: amount.toString(),
                              wallet: walletAddress,
                              credits: selectedTier
                                ? (
                                    selectedTier.base_credit_per_usd *
                                    selectedTier.min_usd_amount
                                  ).toString()
                                : Math.floor(
                                    calculateCredits(amount)
                                  ).toString(),
                              tier: selectedTierForPayment
                                ? selectedTierForPayment.tier_name
                                : "Custom",
                              tier_id: selectedTierForPayment
                                ? selectedTierForPayment.id
                                : null,
                              user_id: user?.uid || "anonymous",
                              payment_type: "node_hosting",
                              payment_method: "stripe",
                              stripe_payment_id: paymentIntent.id,
                              currency: "USD",
                              metadata: {
                                payment_intent_id: paymentIntent.id,
                                payment_method: paymentIntent.payment_method,
                                payment_method_types:
                                  paymentIntent.payment_method_types,
                                status: paymentIntent.status,
                                created: paymentIntent.created,
                                amount_details: paymentIntent.amount_details,
                                capture_method: paymentIntent.capture_method,
                                confirmation_method:
                                  paymentIntent.confirmation_method,
                              },
                              payment_method_details:
                                paymentIntent.payment_method_details || {},
                            };

                            try {
                              const result = await confirmPaymentWithBackend(
                                paymentData
                              );
                              toast.success("Payment successful!");
                              // Close payment method modal
                              setModalStates({ activeModal: null });
                            } catch (error) {
                              console.error(
                                "Payment confirmation error:",
                                error
                              );
                              toast.error(
                                "Payment was successful but confirmation failed. Please contact support."
                              );

                              // Show error modal
                              setShowPaymentModal(true);
                              setPaymentModalContent({
                                title: "Confirmation Failed",
                                message:
                                  "Your payment was successful, but we couldn't confirm it with our servers. Please contact support.",
                                status: "error",
                                details: null,
                              });
                            }
                          }}
                          onError={(message) => {
                            setPaymentStatus({
                              success: null,
                              error: message,
                            });
                            toast.error(message);

                            // Show error modal
                            setShowPaymentModal(true);
                            setPaymentModalContent({
                              title: "Payment Failed",
                              message: message,
                              status: "error",
                              details: null,
                            });
                          }}
                        />
                      </div>
                    </Elements>
                  ) : (
                    <div className="p-4 text-center">
                      <Button
                        onClick={async () => {
                          const finalAmount = selectedTier
                            ? selectedTier.min_usd_amount
                            : Number(customAmount);

                          setLoadingStates((prev) => ({
                            ...prev,
                            stripe: true,
                          }));
                          setErrors((prev) => ({ ...prev, stripe: null }));

                          try {
                            const secret = await createPaymentIntentOnBackend(
                              finalAmount
                            );
                            if (secret) {
                              setClientSecret(secret);
                              toast.info(
                                "Please enter your card details below."
                              );
                            }
                          } catch (error) {
                            setErrors((prev) => ({
                              ...prev,
                              stripe: "Failed to initialize payment",
                            }));
                            toast.error(
                              "Failed to initialize payment. Please try again."
                            );
                          } finally {
                            setLoadingStates((prev) => ({
                              ...prev,
                              stripe: false,
                            }));
                          }
                        }}
                        className="bg-gradient-to-r from-metaxseed-blue to-blue-500 hover:opacity-90 text-white font-medium px-6 py-3 rounded-lg"
                      >
                        {loadingStates.stripe ? (
                          <>
                            <Icon
                              icon="eos-icons:loading"
                              className="w-5 h-5 animate-spin mr-2"
                            />
                            Initializing Payment...
                          </>
                        ) : (
                          <>
                            <Icon
                              icon="material-symbols:credit-card-outline"
                              className="w-5 h-5 mr-2"
                            />
                            Initialize Card Payment
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  {errors.stripe && (
                    <div className="p-4 bg-red-900/30 text-red-300 rounded-lg mt-4">
                      {errors.stripe} - Please try again
                    </div>
                  )}
                  {paymentStatus.success && (
                    <div className="p-4 bg-green-900/30 text-green-300 rounded-lg mt-4">
                      {paymentStatus.success}
                    </div>
                  )}
                  {paymentStatus.error && (
                    <div className="p-4 bg-red-900/30 text-red-300 rounded-lg mt-4">
                      {paymentStatus.error}
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === "crypto" &&
                availablePaymentMethods.includes("crypto") && (
                  <div>
                    <div className="relative">
                      <h2 className="text-sm font-bold mb-2 text-white">
                        Select Token & Network:
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        {renderTokenSelection()}
                        {selectedTokenType && renderNetworkSelection()}
                      </div>

                      {selectedToken && (
                        <div className="mt-2 text-sm">
                          {renderBalanceStatus()}
                          {insufficientBalanceError && (
                            <div className="text-red-400 mt-1">
                              {insufficientBalanceError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Show Complete Payment button immediately after token type selection */}
                    {selectedTokenType && (
                      <div>
                        <Button
                          onClick={handlePaymentSubmit}
                          className={`w-full font-semibold py-3 px-4 rounded-lg transition duration-200 ease-in-out mt-4
                          ${
                            isApprovalPending ||
                            isPaymentPending ||
                            !selectedToken ||
                            (!selectedTier && !customAmount) ||
                            (customAmount &&
                              Number(customAmount) < basicTierMinAmount) ||
                            !isBalanceFetched ||
                            !walletBalance ||
                            walletBalance <
                              (selectedTier
                                ? selectedTier.min_usd_amount
                                : Number(customAmount))
                              ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                              : "bg-gradient-to-r from-yellow-400 to-yellow-600 text-white hover:opacity-90"
                          }
                        `}
                          disabled={
                            isApprovalPending ||
                            isPaymentPending ||
                            !selectedToken ||
                            (!selectedTier && !customAmount) ||
                            (customAmount &&
                              Number(customAmount) < basicTierMinAmount) ||
                            !isBalanceFetched ||
                            !walletBalance ||
                            walletBalance <
                              (selectedTier
                                ? selectedTier.min_usd_amount
                                : Number(customAmount))
                          }
                        >
                          {isApprovalPending || isPaymentPending ? (
                            <>
                              <Icon
                                icon="eos-icons:loading"
                                className="w-5 h-5 animate-spin mr-2"
                              />
                              Processing...
                            </>
                          ) : (
                            "Complete Payment...."
                          )}
                        </Button>

                        {selectedToken &&
                          isBalanceFetched &&
                          walletBalance !== null &&
                          (walletBalance <= 0 ? (
                            <div className="text-red-400 text-xs mt-2 text-center">
                              Your wallet balance is 0. Please deposit funds to
                              continue.
                            </div>
                          ) : (
                            walletBalance <
                              (selectedTier
                                ? selectedTier.min_usd_amount
                                : Number(customAmount)) && (
                              <div className="text-red-400 text-xs mt-2 text-center">
                                Insufficient balance. You need at least $
                                {selectedTier
                                  ? selectedTier.min_usd_amount
                                  : Number(customAmount)}{" "}
                                but have {walletBalance}.
                              </div>
                            )
                          ))}
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div className="border-t border-[#3a3a3c] mt-4 pt-4 px-4 pb-2 flex items-center justify-between">
              <div>
                <div className="text-gray-400 text-sm">Amount:</div>
                <div className="text-white font-bold">
                  $
                  {selectedTier
                    ? Math.floor(selectedTier.min_usd_amount)
                    : Math.floor(Number(customAmount))}
                </div>
              </div>

              <div>
                <div className="text-gray-400 text-sm">Credits:</div>
                <div className="text-white font-bold">
                  {selectedTier
                    ? Math.floor(
                        selectedTier.base_credit_per_usd *
                          selectedTier.min_usd_amount
                      )
                    : Math.floor(calculateCredits(Number(customAmount)))}
                </div>
              </div>
              <Button
                onClick={() => {
                  setModalStates({ activeModal: null });
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg px-4 py-2"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
     
    </div>
  );
}
